/**
 * main.ts - Entry Point
 *
 * Assembles all modules: state, commands, UI, demo player, game loop.
 */

import { Capacitor } from '@capacitor/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { StatusBar, Style } from '@capacitor/status-bar';
import { GameState } from './core/GameState.js';
import { Recorder } from './core/Recorder.js';
import { DemoPlayer } from './core/DemoPlayer.js';
import { applySafeArea } from './ui/SafeArea.js';
import { executor } from './game/commands.js';
import type { GameCommand } from './game/commands.js';
import type { GameStateData, HauntedBuildingSummary, HauntedThemeId } from './game/state.js';
import { INITIAL_STATE, INITIAL_ACADEMY_SKILLS, mapStateToSelectedBuilding, syncSelectedBuildingToState, summarizeHauntedBuildings, getSelectedHauntedBuilding } from './game/state.js';
import { startGameLoop, consumePendingReview, consumeNightTransition, consumeDayAutoTransition, consumePendingEvent, getInGameTime, formatInGameTime } from './game/systems.js';
import type { GhostStaffType } from './game/state.js';
import { GHOST_STAFF_DEFS } from './game/state.js';
import { initI18n, t, showLanguagePicker } from './i18n/index.js';
import { renderBuilding, renderVisitorQueue, updateVisitors } from './ui/buildingView.js';
import { renderShop, renderPrestige, renderFacilities, renderReviews } from './ui/panelViews.js';
import { renderLobbyView } from './ui/lobbyView.js';
import { renderFacilitiesView, showFacilityInfoPopup } from './ui/facilitiesView.js';
import { initBgm, toggleBgm, isBgmOn, setBgmVolume, getBgmVolume } from './audio/bgm.js';
import { playScare, playSkillActivate, playCoin, playPop, playChime, playWhoosh, playChainBonus, toggleSfx, isSfxOn, setSfxVolume } from './audio/sfx.js';
import { renderAcademyView } from './ui/academyView.js';
import { formatSp } from './ui/formatters.js';
import { GHOST_DEFS } from './game/data/ghosts.js';
import { ROOM_TYPES, ALL_ROOM_TYPE_IDS } from './game/data/rooms.js';
import { TRAINING_DEFS, ALL_TRAINING_IDS } from './game/data/trainings.js';
import { PROP_DEFS, DECORATION_PROP_IDS, STAGING_PROP_IDS, propCostForLevel } from './game/data/props.js';
import { CONFIG } from './game/config.js';
import { getEventById } from './game/data/events.js';
import type { ActiveEvent } from './game/state.js';
import { calcRoomStats, calcRoomGrade } from './game/roomStats.js';
import type { RoomGrade } from './game/roomStats.js';
import type { FacilityId } from './game/data/facilities.js';
import type { TrainingId } from './game/data/trainings.js';
import type { PropId } from './game/data/props.js';
import type { MainView } from './game/state.js';
import { EffectLayer } from './effects/EffectLayer.js';
import './style.css';

// === OTA Update ===
if (Capacitor.isNativePlatform()) {
  CapacitorUpdater.addListener('updateAvailable', () => {
    const overlay = document.createElement('div');
    overlay.id = 'ota-loading';
    overlay.innerHTML = '<div class="ota-spinner"></div>';
    document.body.appendChild(overlay);
  });
  CapacitorUpdater.addListener('downloadFailed', () => {
    document.getElementById('ota-loading')?.remove();
  });
}

// === Fullscreen + Safe Area ===
async function setupFullscreen(): Promise<void> {
  applySafeArea();
  if (Capacitor.isNativePlatform()) {
    try {
      await StatusBar.hide();
      await StatusBar.setStyle({ style: Style.Dark });
      await StatusBar.setOverlaysWebView({ overlay: true });
    } catch { /* ignore */ }
  }
}

setupFullscreen();
CapacitorUpdater.notifyAppReady();
initI18n();

// Apply i18n to all elements with data-i18n attribute
function applyI18nToDOM(): void {
  document.querySelectorAll('[data-i18n]').forEach((el) => {
    const key = el.getAttribute('data-i18n');
    if (key) el.textContent = t(key as Parameters<typeof t>[0]);
  });
}
applyI18nToDOM();

// Initialize BGM (Web Audio API)
initBgm();

// === State & Core ===
const gameState = new GameState<GameStateData>(INITIAL_STATE, 'haunted-house-save');

// Migrate old saves: merge new default fields into existing saves
{
  const saved = gameState.get();
  const CURRENT_SAVE_VERSION = 19;
  const savedVersion = (saved as any)._saveVersion ?? 0;
  const needsMigration = savedVersion < CURRENT_SAVE_VERSION;

  if (needsMigration) {
    // Merge: INITIAL_STATE first, then saved overrides, but filter out undefined values
    const cleanSaved = Object.fromEntries(
      Object.entries(saved).filter(([_, v]) => v !== undefined)
    );
    const migrated = { ...INITIAL_STATE, ...cleanSaved } as GameStateData;
    // Migrate old rooms[] array to single room field
    migrated.building = migrated.building.map((floor: any) => {
      if (Array.isArray(floor.rooms)) {
        const firstRoom = floor.rooms.find((r: any) => r !== null) ?? null;
        return { level: floor.level, room: firstRoom };
      }
      if (floor.room === undefined) {
        return { level: floor.level, room: null };
      }
      return floor;
    });
    // Ensure building has minimum floors
    if (migrated.building.length < INITIAL_STATE.building.length) {
      migrated.building = [...INITIAL_STATE.building];
    }
    // Migrate renamed ghost character IDs
    const ghostIdRemap: Record<string, string> = {
      dracula: 'blood_count',
      sadako: 'well_spirit',
      freddy: 'nightmare_specter',
      slenderman: 'shadow_stalker',
    };
    // Migrate ghost instances to include new fields
    migrated.ghosts = migrated.ghosts.map((g: any) => ({
      ...g,
      characterId: ghostIdRemap[g.characterId] ?? g.characterId,
      role: g.role ?? 'scare',
      assignedFacilityId: g.assignedFacilityId ?? null,
      academySession: g.academySession ?? null,
      academySkills: g.academySkills ?? { ...INITIAL_ACADEMY_SKILLS },
      hiredAt: g.hiredAt ?? 1,
      praiseCount: g.praiseCount ?? 0,
    }));
    // Migrate rooms to include condition/hygiene and new fields
    migrated.building = migrated.building.map((floor: any) => ({
      ...floor,
      room: floor.room ? {
        ...floor.room,
        condition: floor.room.condition ?? 100,
        hygiene: floor.room.hygiene ?? 100,
        conditionUpgrade: floor.room.conditionUpgrade ?? 0,
        hygieneUpgrade: floor.room.hygieneUpgrade ?? 0,
        collapsed: floor.room.collapsed ?? false,
        underRepair: floor.room.underRepair ?? false,
        underCleaning: floor.room.underCleaning ?? false,
        repairEndsAt: floor.room.repairEndsAt ?? 0,
        cleaningEndsAt: floor.room.cleaningEndsAt ?? 0,
        props: floor.room.props ?? [],
      } : null,
    }));
    // Migrate star evolution fields
    if (migrated.starLevel === undefined) migrated.starLevel = 0;
    if (migrated.starEvolution === undefined) migrated.starEvolution = 0;
    // Migrate facilities to include assignedStaffId
    migrated.facilities = (migrated.facilities || []).map((f: any) => ({
      ...f,
      assignedStaffId: f.assignedStaffId ?? null,
    }));
    // Migrate lobby building to unified format
    if (!migrated.lobbyBuilding || migrated.lobbyBuilding.length === 0) {
      migrated.lobbyBuilding = [...INITIAL_STATE.lobbyBuilding];
    } else {
      // Migrate old LobbyFloor format (type/upgradeLevel) to new (facilityType/facilityLevel)
      migrated.lobbyBuilding = migrated.lobbyBuilding.map((f: any) => {
        if (f.facilityType === undefined) {
          // Old format: has 'type' and 'upgradeLevel'
          return {
            level: f.level,
            facilityType: f.type || null,
            facilityLevel: f.upgradeLevel || 1,
            assignedGhostId: f.assignedGhostId || null,
            props: f.props || [],
          };
        }
        return f;
      });
      // Ensure at least 3 floors
      while (migrated.lobbyBuilding.length < 3) {
        migrated.lobbyBuilding.push({
          level: migrated.lobbyBuilding.length + 1,
          facilityType: null,
          facilityLevel: 0,
          assignedGhostId: null,
          props: [],
        });
      }
    }
    // Ensure floor 1 always has ticket_booth (permanent fixture)
    const floor1 = migrated.lobbyBuilding.find((f: any) => f.level === 1);
    if (floor1 && floor1.facilityType !== 'ticket_booth') {
      floor1.facilityType = 'ticket_booth';
      floor1.facilityLevel = floor1.facilityLevel || 1;
    }
    // Migrate lobbyFacilities data into lobbyBuilding floors
    if (migrated.lobbyFacilities && migrated.lobbyFacilities.length > 0) {
      for (const fac of migrated.lobbyFacilities) {
        // Skip ticket_booth since it's already on floor 1
        if (fac.type === 'ticket_booth') continue;
        // Check if this facility type is already in a floor
        const alreadyInFloor = migrated.lobbyBuilding.some(
          (f: any) => f.facilityType === fac.type,
        );
        if (alreadyInFloor) continue;
        // Find first empty floor
        const emptyFloor = migrated.lobbyBuilding.find(
          (f: any) => f.facilityType === null,
        );
        if (emptyFloor) {
          emptyFloor.facilityType = fac.type;
          emptyFloor.facilityLevel = fac.level || 1;
          if (fac.assignedStaffId) {
            emptyFloor.assignedGhostId = fac.assignedStaffId;
          }
        } else {
          // No empty floor: add a new floor
          migrated.lobbyBuilding.push({
            level: migrated.lobbyBuilding.length + 1,
            facilityType: fac.type,
            facilityLevel: fac.level || 1,
            assignedGhostId: fac.assignedStaffId || null,
            props: [],
          });
        }
      }
      // Clear lobbyFacilities (keep empty array, do not delete)
      migrated.lobbyFacilities = [];
    }
    // Migrate facility building from existing facilities
    if (!migrated.facilityBuilding) {
      migrated.facilityBuilding = [];
    }
    // Sync existing facilities into facilityBuilding if missing
    if (migrated.facilityBuilding.length === 0 && migrated.facilities.length > 0) {
      migrated.facilityBuilding = migrated.facilities.map((f: any, idx: number) => ({
        level: idx + 1,
        type: f.facilityId,
        upgradeLevel: f.level ?? 1,
        assignedGhostId: f.assignedStaffId ?? null,
        props: [],
      }));
    }
    // Migrate to facility map (plot-based grid)
    if (!migrated.facilityMap || migrated.facilityMap.length === 0) {
      migrated.facilityMap = INITIAL_STATE.facilityMap.map((plot: any) => ({ ...plot }));
      // If there were existing facilityBuilding floors, place them into plots
      if (migrated.facilityBuilding.length > 0) {
        for (let i = 0; i < migrated.facilityBuilding.length && i < 12; i++) {
          const floor = migrated.facilityBuilding[i];
          migrated.facilityMap[i] = {
            id: i,
            unlocked: true,
            facility: {
              type: floor.type,
              upgradeLevel: floor.upgradeLevel ?? 1,
              assignedGhostId: floor.assignedGhostId ?? null,
              props: floor.props ?? [],
              price: 0,
              totalRevenue: 0,
              isOpen: true,
              totalRatings: 0,
              ratingSum: 0,
              visitorCount: 0,
            },
          };
        }
      }
    }
    // Expand facilityMap to 16 plots if needed
    if (migrated.facilityMap.length < 16) {
      const oldLen = migrated.facilityMap.length;
      for (let i = oldLen; i < 16; i++) {
        migrated.facilityMap.push({ id: i, unlocked: false, facility: null });
      }
      // Ensure at least first 4 plots are unlocked
      for (let i = 0; i < 4; i++) {
        if (migrated.facilityMap[i]) {
          migrated.facilityMap[i].unlocked = true;
        }
      }
    }
    // Migrate facility plots to include price/totalRevenue
    migrated.facilityMap = migrated.facilityMap.map((p: any) => ({
      ...p,
      facility: p.facility ? {
        ...p.facility,
        price: p.facility.price ?? 0,
        totalRevenue: p.facility.totalRevenue ?? 0,
        isOpen: p.facility.isOpen ?? true,
        totalRatings: p.facility.totalRatings ?? 0,
        ratingSum: p.facility.ratingSum ?? 0,
        visitorCount: p.facility.visitorCount ?? 0,
      } : null,
    }));
    // Migrate day phase cycle
    if ((migrated as any).dayPhase === undefined) {
      (migrated as any).dayPhase = 'day';
      (migrated as any).dayPhaseStartedAt = Date.now();
      (migrated as any).gameSpeed = 1;
    }
    // Migrate humanStaff → ghostStaff (v16)
    if ((migrated as any).humanStaff !== undefined) {
      (migrated as any).ghostStaff = (migrated as any).humanStaff;
      delete (migrated as any).humanStaff;
    }
    if ((migrated as any).ghostStaff === undefined) {
      (migrated as any).ghostStaff = [];
    }
    if ((migrated as any).todaySalaries === undefined) {
      (migrated as any).todaySalaries = 0;
    }
    // Migrate: add initial ghosts + ghost staff for existing saves with 0 of each
    if (migrated.ghosts.length === 0 && migrated.ghostStaff.length === 0) {
      migrated.ghosts = [
        ...INITIAL_STATE.ghosts.map((g) => ({ ...g, academySkills: { ...INITIAL_ACADEMY_SKILLS } })),
      ];
      migrated.ghostStaff = [...INITIAL_STATE.ghostStaff];
    }
    // Migrate: lobby waiting room system
    if (!(migrated as any).lobbyWaitQueue) {
      (migrated as any).lobbyWaitQueue = [];
    }
    if (!(migrated as any).maxQueueSize) {
      (migrated as any).maxQueueSize = 10;
    }
    if (!(migrated as any).lobbyFacilities || (migrated as any).lobbyFacilities.length === 0) {
      (migrated as any).lobbyFacilities = [...INITIAL_STATE.lobbyFacilities];
    }
    if ((migrated as any).queueTickAccumMs === undefined) {
      (migrated as any).queueTickAccumMs = 0;
    }
    if ((migrated as any).queueSpawnAccumMs === undefined) {
      (migrated as any).queueSpawnAccumMs = 0;
    }
    if (!(migrated as any).hauntedBuildingMetas || (migrated as any).hauntedBuildingMetas.length === 0) {
      (migrated as any).hauntedBuildingMetas = [...INITIAL_STATE.hauntedBuildingMetas];
    }
    // Expand building slots to 10 (was 3 in older saves)
    const existingMetas = (migrated as any).hauntedBuildingMetas as any[];
    if (existingMetas.length < 10) {
      for (let i = existingMetas.length + 1; i <= 10; i++) {
        existingMetas.push({
          id: `building_${i}`,
          name: '',
          themeId: null,
          buildingExteriorAsset: '',
          lobbyStyleId: '',
          passiveRevenueMultiplier: 0.7,
          maxFloors: 5,
          isUnlocked: false,
        });
      }
    }
    if (!(migrated as any).hauntedBuildings || (migrated as any).hauntedBuildings.length === 0) {
      const selectedId = (migrated as any).selectedHauntedBuildingId || 'building_1';
      const selectedMeta = ((migrated as any).hauntedBuildingMetas || INITIAL_STATE.hauntedBuildingMetas)
        .find((meta: any) => meta.id === selectedId) || INITIAL_STATE.hauntedBuildingMetas[0];
      (migrated as any).selectedHauntedBuildingId = selectedMeta.id;
      (migrated as any).worldScreen = (migrated as any).worldScreen || 'world';
      (migrated as any).hauntedBuildings = [{
        id: selectedMeta.id,
        name: selectedMeta.name,
        themeId: selectedMeta.themeId,
        maxFloors: selectedMeta.maxFloors,
        buildingExteriorAsset: selectedMeta.buildingExteriorAsset,
        lobbyStyleId: selectedMeta.lobbyStyleId,
        passiveRevenueMultiplier: selectedMeta.passiveRevenueMultiplier,
        building: migrated.building,
        visitorGroup: migrated.visitorGroup,
        reviews: migrated.reviews,
        isOpen: migrated.isOpen,
        ticketPrice: migrated.ticketPrice,
        dayNumber: migrated.dayNumber,
        dayStartTime: migrated.dayStartTime,
        todayVisitors: migrated.todayVisitors,
        todayEarnings: migrated.todayEarnings,
        reputation: migrated.reputation,
        recommendationPool: migrated.recommendationPool,
        todayFleeCount: migrated.todayFleeCount,
        lobbyQueue: migrated.lobbyQueue,
        activeTeams: migrated.activeTeams,
        maxConcurrentTeams: migrated.maxConcurrentTeams,
        lobbyStaff: migrated.lobbyStaff,
        lobbyWaitQueue: migrated.lobbyWaitQueue,
        maxQueueSize: migrated.maxQueueSize,
        lobbyFacilities: migrated.lobbyFacilities,
        queueTickAccumMs: migrated.queueTickAccumMs,
        queueSpawnAccumMs: migrated.queueSpawnAccumMs,
        lobbyBuilding: migrated.lobbyBuilding,
        facilityBuilding: migrated.facilityBuilding,
        facilityMap: migrated.facilityMap,
        storyBonus: migrated.storyBonus,
        storyGrade: migrated.storyGrade,
        activeEvent: migrated.activeEvent,
        eventHistory: migrated.eventHistory,
        eventCooldownUntil: migrated.eventCooldownUntil,
        eventTickAccumMs: migrated.eventTickAccumMs,
      }];
    }
    // v18: Sync hauntedBuildings themeId/assets from metas (fix theme mismatch bug)
    if (migrated.hauntedBuildings && migrated.hauntedBuildingMetas) {
      migrated.hauntedBuildings = migrated.hauntedBuildings.map((building: any) => {
        const meta = migrated.hauntedBuildingMetas.find((m: any) => m.id === building.id);
        if (meta && meta.themeId && meta.themeId !== building.themeId) {
          return {
            ...building,
            themeId: meta.themeId,
            buildingExteriorAsset: meta.buildingExteriorAsset,
            lobbyStyleId: meta.lobbyStyleId,
          };
        }
        return building;
      });
    }
    // Migrate: street visitor system
    if (!(migrated as any).streetVisitors) {
      (migrated as any).streetVisitors = [];
      (migrated as any).streetSpawnAccumMs = 0;
      (migrated as any).maxStreetVisitors = 8;
      (migrated as any).streetAiAccumMs = 0;
    }
    // Migrate: word-of-mouth + season event system
    if ((migrated as any).wordOfMouth === undefined) {
      (migrated as any).wordOfMouth = 1.0;
      (migrated as any).seasonEvent = null;
      (migrated as any).seasonEventStartedAt = 0;
    }
    // Migrate: street visitor visitorType field
    if (Array.isArray((migrated as any).streetVisitors)) {
      (migrated as any).streetVisitors = (migrated as any).streetVisitors.map((v: any) => {
        if (!v.visitorType) return { ...v, visitorType: 'thrill_seeker' };
        return v;
      });
    }

    // Migrate: add ticket_booth to plot 1 if not present
    if (Array.isArray((migrated as any).facilityMap)) {
      const hasTicketBooth = (migrated as any).facilityMap.some((p: any) => p.facility?.type === 'ticket_booth');
      if (!hasTicketBooth) {
        // Find first empty unlocked plot (prefer plot 1, then any empty)
        const fmap = (migrated as any).facilityMap;
        let targetPlot = fmap[1]?.facility ? null : fmap[1];
        if (!targetPlot) {
          targetPlot = fmap.find((p: any) => p.unlocked && !p.facility);
        }
        if (targetPlot) {
          targetPlot.facility = {
            type: 'ticket_booth',
            upgradeLevel: 1,
            assignedGhostId: null,
            props: [],
            price: 0,
            totalRevenue: 0,
            isOpen: true,
            totalRatings: 0,
            ratingSum: 0,
            visitorCount: 0,
          };
        }
      }
    }

    const selectedState = mapStateToSelectedBuilding(migrated);
    (selectedState as any)._saveVersion = CURRENT_SAVE_VERSION;
    gameState.set(selectedState);
  } else if (savedVersion === 0) {
    // First time: just stamp version without migrating
    gameState.patch({ _saveVersion: CURRENT_SAVE_VERSION } as any);
  }
}

const recorder = new Recorder<GameCommand>('haunted-house');
const demoPlayer = new DemoPlayer<GameStateData, GameCommand>(executor, gameState);

// === DOM References ===
const spDisplay = document.getElementById('sp-display')!;
const fameDisplay = document.getElementById('fame-display')!;
const dayDisplay = document.getElementById('day-display')!;
const buildingEl = document.getElementById('building')!;
const visitorQueueEl = document.getElementById('visitor-queue')!;
// Tab popup content containers (created dynamically)
let tabPopupContentEl: HTMLElement | null = null;

// View system DOM references
const worldScreen = document.getElementById('world-screen')!;
const worldContent = document.getElementById('world-content')!;
const buildingContainer = document.getElementById('building-container')!;
const lobbyContainer = document.getElementById('lobby-container')!;
const facilitiesContainer = document.getElementById('facilities-container')!;
const academyContainer = document.getElementById('academy-container')!;
const lobbyContent = document.getElementById('lobby-content')!;
const facilitiesContent = document.getElementById('facilities-content')!;
const academyContent = document.getElementById('academy-content')!;
const popupContainer = document.getElementById('popup-container')!;

// === PixiJS Effect Layer ===
const effectLayer = new EffectLayer();
let effectLayerInitialized = false;
let prevVisitorFloorIndex = -1;

// Create and mount effect canvas
{
  const effectCanvas = document.createElement('canvas');
  effectCanvas.id = 'effect-canvas';
  effectCanvas.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:5;max-width:100vw;max-height:100vh;';
  const buildingInner = document.getElementById('building-inner');
  if (buildingInner) {
    buildingInner.style.position = 'relative';
    buildingInner.appendChild(effectCanvas);
    effectLayer.init(effectCanvas).then(() => {
      effectLayerInitialized = true;
    }).catch(() => {
      // PixiJS init failed (e.g., no WebGL support), continue without effects
    });
  }
}

// === Building Drag-to-Scroll ===
{
  let y0 = 0, s0 = 0, active = false, didDrag = false;
  let dragResetTimer: ReturnType<typeof setTimeout> | null = null;

  const resetDrag = () => {
    if (dragResetTimer) clearTimeout(dragResetTimer);
    dragResetTimer = setTimeout(() => { didDrag = false; }, 300);
  };

  buildingContainer.addEventListener('mousedown', (e) => {
    y0 = e.clientY; s0 = buildingContainer.scrollTop; active = true; didDrag = false;
    e.preventDefault();
  });
  document.addEventListener('mousemove', (e) => {
    if (!active) return;
    const dy = e.clientY - y0;
    if (Math.abs(dy) > 10) didDrag = true;
    buildingContainer.scrollTop = s0 - dy;
  });
  document.addEventListener('mouseup', () => { active = false; resetDrag(); });

  buildingContainer.addEventListener('touchstart', (e) => {
    y0 = e.touches[0].clientY; s0 = buildingContainer.scrollTop; active = true; didDrag = false;
  }, { passive: true });
  buildingContainer.addEventListener('touchmove', (e) => {
    if (!active) return;
    const dy = e.touches[0].clientY - y0;
    if (Math.abs(dy) > 10) didDrag = true;
    buildingContainer.scrollTop = s0 - dy;
    if (didDrag) e.preventDefault();
  }, { passive: false });
  buildingContainer.addEventListener('touchend', () => { active = false; resetDrag(); }, { passive: true });
  buildingContainer.addEventListener('touchcancel', () => { active = false; didDrag = false; }, { passive: true });

  buildingContainer.addEventListener('click', (e) => {
    if (didDrag) { e.stopPropagation(); e.preventDefault(); didDrag = false; }
  });
}

// === Tab Management (Bottom Sheet Popup) ===
let currentTab: string | null = null;
let tabPopupOverlay: HTMLElement | null = null;
let tabPopupSheet: HTMLElement | null = null;
let tabPopupClosing = false;

const TAB_TITLES: Record<string, Parameters<typeof t>[0]> = {
  facilities_mgmt: 'tab_facilities',
  hire: 'tab_hire',
  staff: 'tab_staff',
  menu: 'menu_tab',
  support_facilities: 'tab_support_facilities',
  achievements: 'tab_achievements',
};

function getThemeLabel(summary: Pick<HauntedBuildingSummary, 'themeId'>): string {
  switch (summary.themeId) {
    case 'abandoned_hospital': return t('theme_abandoned_hospital');
    case 'school_back_mountain': return t('theme_school_back_mountain');
    case 'haunted_house': return t('theme_haunted_house');
    case 'cursed_circus': return t('theme_cursed_circus');
    case 'ancient_temple': return t('theme_ancient_temple');
    case 'pirate_ship': return t('theme_pirate_ship');
    case 'abandoned_mine': return t('theme_abandoned_mine');
    case 'gothic_castle': return t('theme_gothic_castle');
    case 'underground_bunker': return t('theme_underground_bunker');
    case 'cursed_forest': return t('theme_cursed_forest');
    default: return t('construction_theme_label');
  }
}

interface ThemeChoice {
  id: HauntedThemeId;
  label: string;
  description: string;
  icon: string;
  locked: boolean;
  unlockFame: number;
}

function getThemeChoices(): ThemeChoice[] {
  const fame = gameState.get().fame ?? 0;
  const themes: Array<{ id: HauntedThemeId; label: string; description: string; icon: string; unlockFame: number }> = [
    // 5 free themes
    { id: 'haunted_house', label: t('theme_haunted_house'), description: t('theme_haunted_house_desc'), icon: '/images/building-bg.webp', unlockFame: 0 },
    { id: 'abandoned_hospital', label: t('theme_abandoned_hospital'), description: t('theme_abandoned_hospital_desc'), icon: '/images/views/facility-street-bg.webp', unlockFame: 0 },
    { id: 'school_back_mountain', label: t('theme_school_back_mountain'), description: t('theme_school_back_mountain_desc'), icon: '/images/views/lobby-bg.webp', unlockFame: 0 },
    { id: 'cursed_circus', label: t('theme_cursed_circus'), description: t('theme_cursed_circus_desc'), icon: '/images/views/cursed-circus-bg.webp', unlockFame: 0 },
    { id: 'ancient_temple', label: t('theme_ancient_temple'), description: t('theme_ancient_temple_desc'), icon: '/images/views/ancient-temple-bg.webp', unlockFame: 0 },
    // Special themes — Fame 100: 2 unlock
    { id: 'pirate_ship', label: t('theme_pirate_ship'), description: t('theme_pirate_ship_desc'), icon: '/images/views/pirate-ship-bg.webp', unlockFame: 100 },
    { id: 'abandoned_mine', label: t('theme_abandoned_mine'), description: t('theme_abandoned_mine_desc'), icon: '/images/views/abandoned-mine-bg.webp', unlockFame: 100 },
    // Fame 300: 2 unlock
    { id: 'gothic_castle', label: t('theme_gothic_castle'), description: t('theme_gothic_castle_desc'), icon: '/images/views/gothic-castle-bg.webp', unlockFame: 300 },
    { id: 'underground_bunker', label: t('theme_underground_bunker'), description: t('theme_underground_bunker_desc'), icon: '/images/views/underground-bunker-bg.webp', unlockFame: 300 },
    // Fame 500: 1 unlock
    { id: 'cursed_forest', label: t('theme_cursed_forest'), description: t('theme_cursed_forest_desc'), icon: '/images/views/cursed-forest-bg.webp', unlockFame: 500 },
  ];
  return themes.map((th) => ({ ...th, locked: fame < th.unlockFame }));
}

function getWorldStatusLabel(summary: HauntedBuildingSummary): string {
  if (!summary.isUnlocked) return t('world_status_build');
  return summary.isOpen ? t('world_status_open') : t('world_status_ready');
}

function showHauntedBuildingConstructionPopup(summary: HauntedBuildingSummary): void {
  const existing = document.getElementById('building-construction-popup');
  existing?.remove();

  const cost = 5000 * Number(summary.id.replace('building_', ''));
  let selectedTheme: HauntedThemeId | null = summary.themeId;

  const overlay = document.createElement('div');
  overlay.id = 'building-construction-popup';
  overlay.className = 'popup-overlay';
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) overlay.remove();
  });

  const box = document.createElement('div');
  box.className = 'popup-box construction-popup';

  // === Fixed top bar: X button + Build(cost) button ===
  const topBar = document.createElement('div');
  topBar.className = 'construction-topbar';

  const closeBtn = document.createElement('button');
  closeBtn.className = 'construction-close-btn';
  closeBtn.textContent = '\u00d7';
  closeBtn.addEventListener('click', () => overlay.remove());
  topBar.appendChild(closeBtn);

  const buildBtn = document.createElement('button');
  const canAfford = gameState.get().sp >= cost;
  buildBtn.className = `construction-buy-btn${canAfford ? ' affordable' : ''}`;
  buildBtn.innerHTML = `${t('construction_start')} <img src="/images/ui/ui-coin-sp.webp" alt="" draggable="false" /><span>${formatSp(cost)}</span>`;
  buildBtn.addEventListener('click', () => {
    const nameInput = box.querySelector<HTMLInputElement>('.construction-input');
    const trimmedName = nameInput?.value.trim() ?? '';
    if (!trimmedName || !selectedTheme) return;
    const spNow = gameState.get().sp;
    if (spNow < cost) {
      buildBtn.classList.add('shake');
      const span = buildBtn.querySelector('span');
      if (span) span.textContent = t('not_enough_sp');
      setTimeout(() => {
        buildBtn.classList.remove('shake');
        if (span) span.textContent = `${formatSp(cost)} SP`;
      }, 1200);
      return;
    }
    dispatch({ type: 'SET_HAUNTED_BUILDING_NAME', payload: { buildingId: summary.id, name: trimmedName }, timestamp: Date.now() });
    dispatch({ type: 'SET_HAUNTED_BUILDING_THEME', payload: { buildingId: summary.id, themeId: selectedTheme }, timestamp: Date.now() });
    dispatch({ type: 'UNLOCK_HAUNTED_BUILDING', payload: { buildingId: summary.id }, timestamp: Date.now() });
    dispatch({ type: 'SELECT_HAUNTED_BUILDING', payload: { buildingId: summary.id }, timestamp: Date.now() });
    overlay.remove();
  });
  topBar.appendChild(buildBtn);
  box.appendChild(topBar);

  // === Scrollable content ===
  const scrollArea = document.createElement('div');
  scrollArea.className = 'construction-scroll';

  // Header
  const header = document.createElement('div');
  header.className = 'construction-header';
  header.innerHTML = `
    <img src="/images/ghost/banshee-idle.webp" alt="" class="construction-mascot" draggable="false" />
    <div class="construction-header-text">
      <div class="construction-title">${t('construction_title')}</div>
      <div class="construction-subtitle">${t('construction_subtitle')}</div>
    </div>
  `;
  scrollArea.appendChild(header);

  // Name input
  const nameSection = document.createElement('div');
  nameSection.className = 'construction-section';
  nameSection.innerHTML = `<div class="construction-label">${t('construction_name_label')}</div>`;
  const nameInput = document.createElement('input');
  nameInput.type = 'text';
  nameInput.maxLength = 24;
  nameInput.placeholder = t('construction_name_placeholder');
  nameInput.value = summary.name;
  nameInput.className = 'construction-input';
  nameSection.appendChild(nameInput);
  scrollArea.appendChild(nameSection);

  // Theme selection
  const themeSection = document.createElement('div');
  themeSection.className = 'construction-section';
  themeSection.innerHTML = `<div class="construction-label">${t('construction_theme_label')}</div>`;

  const themeList = document.createElement('div');
  themeList.className = 'construction-theme-list';

  const renderThemeButtons = (): void => {
    themeList.innerHTML = '';
    getThemeChoices().forEach((theme) => {
      const isSelected = selectedTheme === theme.id;
      const card = document.createElement('button');
      card.type = 'button';
      if (theme.locked) {
        card.className = 'construction-theme-card locked';
        card.innerHTML = `
          <div class="ctc-thumb ctc-thumb-locked">
            <img src="${theme.icon}" alt="" draggable="false" />
            <div class="ctc-lock-overlay"></div>
          </div>
          <div class="ctc-info">
            <div class="ctc-label">${theme.label}</div>
            <div class="ctc-unlock-req">Fame ${theme.unlockFame}</div>
          </div>
        `;
      } else {
        card.className = `construction-theme-card ${isSelected ? 'selected' : ''}`;
        card.innerHTML = `
          <div class="ctc-thumb">
            <img src="${theme.icon}" alt="" draggable="false" />
            ${isSelected ? '<div class="ctc-check">\u2713</div>' : ''}
          </div>
          <div class="ctc-info">
            <div class="ctc-label">${theme.label}</div>
            <div class="ctc-desc">${theme.description}</div>
          </div>
        `;
        card.addEventListener('click', () => {
          selectedTheme = theme.id;
          renderThemeButtons();
        });
      }
      themeList.appendChild(card);
    });
  };
  renderThemeButtons();
  themeSection.appendChild(themeList);
  scrollArea.appendChild(themeSection);

  box.appendChild(scrollArea);
  overlay.appendChild(box);
  popupContainer.appendChild(overlay);
}

function showRenameBuildingPopup(buildingId: string, currentName: string): void {
  const overlay = document.createElement('div');
  overlay.className = 'popup-overlay';
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });

  const box = document.createElement('div');
  box.className = 'popup-box';
  box.style.cssText = 'max-width:320px;padding:20px;display:flex;flex-direction:column;gap:12px;';

  const title = document.createElement('div');
  title.className = 'popup-title';
  title.textContent = t('rename_building');
  box.appendChild(title);

  const input = document.createElement('input');
  input.type = 'text';
  input.maxLength = 24;
  input.value = currentName;
  input.placeholder = t('rename_building_desc');
  input.style.cssText = 'width:100%;padding:12px 14px;border-radius:10px;border:1px solid rgba(255,255,255,0.14);background:#120d1b;color:#f5ecff;font-size:14px;box-sizing:border-box;';
  box.appendChild(input);

  const btnRow = document.createElement('div');
  btnRow.style.cssText = 'display:flex;gap:8px;';

  const cancelBtn = document.createElement('button');
  cancelBtn.className = 'popup-btn secondary';
  cancelBtn.style.cssText = 'flex:1;min-height:44px;';
  cancelBtn.textContent = t('cancel');
  cancelBtn.addEventListener('click', () => overlay.remove());
  btnRow.appendChild(cancelBtn);

  const confirmBtn = document.createElement('button');
  confirmBtn.className = 'popup-btn primary';
  confirmBtn.style.cssText = 'flex:1;min-height:44px;';
  confirmBtn.textContent = t('rename');
  confirmBtn.addEventListener('click', () => {
    const trimmed = input.value.trim();
    if (!trimmed) return;
    dispatch({ type: 'SET_HAUNTED_BUILDING_NAME', payload: { buildingId, name: trimmed }, timestamp: Date.now() });
    overlay.remove();
  });
  btnRow.appendChild(confirmBtn);

  box.appendChild(btnRow);
  overlay.appendChild(box);
  popupContainer.appendChild(overlay);

  // Auto-focus input
  setTimeout(() => input.focus(), 100);
}

let _lastWorldScreenHash = '';

function renderWorldScreen(state: GameStateData): void {
  const allSummaries = summarizeHauntedBuildings(state);
  // Progressive reveal: start with 5 slots, +1 per additional unlocked building
  const unlockedCount = allSummaries.filter(s => s.isUnlocked).length;
  const visibleCount = Math.min(allSummaries.length, Math.max(5, unlockedCount + 4));
  const summaries = allSummaries.slice(0, visibleCount);

  // Skip re-render if nothing changed (prevents click event loss from DOM recreation)
  const hash = JSON.stringify(summaries.map(s => ({
    id: s.id, name: s.name, isUnlocked: s.isUnlocked, isOpen: s.isOpen,
    floorsBuilt: s.floorsBuilt, maxFloors: s.maxFloors, todayEarnings: s.todayEarnings,
    queueCount: s.queueCount, themeId: s.themeId,
  })));
  if (hash === _lastWorldScreenHash && worldContent.children.length > 0) return;
  _lastWorldScreenHash = hash;

  worldContent.innerHTML = '';

  // Street background with night atmosphere
  worldContent.className = 'world-street-view';

  const header = document.createElement('div');
  header.className = 'world-street-header';
  header.innerHTML = `<div class="world-street-title">${t('world_title')}</div><div class="world-street-subtitle">${t('world_subtitle')}</div>`;
  worldContent.appendChild(header);

  const street = document.createElement('div');
  street.className = 'world-street-road';

  summaries.forEach((summary) => {
    const card = document.createElement('button');
    card.type = 'button';
    card.className = `world-building-card ${summary.isUnlocked ? 'unlocked' : 'locked'}`;

    const themeImg = getThemeThumbnail(summary);
    const status = getWorldStatusLabel(summary);
    const statusClass = summary.isUnlocked ? (summary.isOpen ? 'open' : 'ready') : 'locked';

    const buildingNum = Number(summary.id.replace('building_', ''));
    const unlockCost = 5000 * buildingNum;

    card.innerHTML = summary.isUnlocked ? `
      <div class="wbc-thumbnail">
        <img src="${themeImg}" alt="" draggable="false" />
        <div class="wbc-thumbnail-overlay"></div>
        <div class="wbc-status-badge ${statusClass}">${status}</div>
      </div>
      <div class="wbc-info">
        <div class="wbc-name-row">
          <div class="wbc-name">${summary.name}</div>
          <button class="wbc-rename-btn" data-building-id="${summary.id}" data-building-name="${summary.name}">\u270E</button>
        </div>
        <div class="wbc-theme">${getThemeLabel(summary)}</div>
        <div class="wbc-stats">
          <div class="wbc-stat">
            <img src="/images/ui/ui-icon-ghost.webp" alt="" draggable="false" class="wbc-stat-icon" />
            <span>${summary.floorsBuilt}/${summary.maxFloors}</span>
          </div>
          <div class="wbc-stat">
            <img src="/images/ui/ui-coin-sp.webp" alt="" draggable="false" class="wbc-stat-icon" />
            <span>${formatSp(summary.todayEarnings)}</span>
          </div>
          <div class="wbc-stat">
            <img src="/images/ui/icon-visitor.webp" alt="" draggable="false" class="wbc-stat-icon" />
            <span>${summary.queueCount}</span>
          </div>
        </div>
      </div>
    ` : `
      <div class="wbc-thumbnail wbc-thumbnail-locked">
        <div class="wbc-lock-icon">+</div>
        <div class="wbc-status-badge locked">${t('build_room')}</div>
      </div>
      <div class="wbc-info">
        <div class="wbc-name-row">
          <div class="wbc-name">${t('construction_title')}</div>
        </div>
        <div class="wbc-cost-row">
          <img src="/images/ui/ui-coin-sp.webp" alt="" draggable="false" class="wbc-stat-icon" />
          <span>${formatSp(unlockCost)} SP</span>
        </div>
      </div>
    `;

    if (summary.isUnlocked) {
      // Rename button handler (prevent card click propagation)
      const renameBtn = card.querySelector('.wbc-rename-btn');
      if (renameBtn) {
        renameBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          showRenameBuildingPopup(summary.id, summary.name);
        });
      }
      card.addEventListener('click', () => {
        dispatch({ type: 'SELECT_HAUNTED_BUILDING', payload: { buildingId: summary.id }, timestamp: Date.now() });
      });
    } else {
      card.addEventListener('click', () => {
        showHauntedBuildingConstructionPopup(summary);
      });
    }
    street.appendChild(card);
  });

  worldContent.appendChild(street);
}

function getThemeThumbnail(summary: HauntedBuildingSummary): string {
  const themeMap: Record<string, string> = {
    haunted_house: '/images/building-bg.webp',
    abandoned_hospital: '/images/views/facility-street-bg.webp',
    school_back_mountain: '/images/views/lobby-bg.webp',
    cursed_circus: '/images/views/cursed-circus-bg.webp',
    ancient_temple: '/images/views/ancient-temple-bg.webp',
    pirate_ship: '/images/views/pirate-ship-bg.webp',
    abandoned_mine: '/images/views/abandoned-mine-bg.webp',
    gothic_castle: '/images/views/gothic-castle-bg.webp',
    underground_bunker: '/images/views/underground-bunker-bg.webp',
    cursed_forest: '/images/views/cursed-forest-bg.webp',
  };
  if (summary.isUnlocked && summary.themeId) {
    return themeMap[summary.themeId] ?? '/images/building-bg.webp';
  }
  return '/images/building-entrance.webp';
}

let _worldBackBtn: HTMLButtonElement | null = null;

function renderWorldBackButton(state: GameStateData): void {
  if (state.worldScreen === 'world') {
    if (_worldBackBtn) {
      _worldBackBtn.style.display = 'none';
    }
    return;
  }

  const viewSwitcher = document.getElementById('view-switcher');
  if (!viewSwitcher) return;

  if (!_worldBackBtn) {
    _worldBackBtn = document.createElement('button');
    _worldBackBtn.id = 'world-back-btn';
    _worldBackBtn.type = 'button';
    _worldBackBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      dispatch({ type: 'SET_WORLD_SCREEN', payload: { screen: 'world' }, timestamp: Date.now() });
    });
    viewSwitcher.prepend(_worldBackBtn);
  }

  const label = t('view_other_buildings');
  if (_worldBackBtn.textContent !== label) {
    _worldBackBtn.textContent = label;
  }
  if (_worldBackBtn.style.display === 'none') {
    _worldBackBtn.style.display = '';
  }

  // Ensure it's still the first child of view-switcher
  if (_worldBackBtn.parentElement !== viewSwitcher) {
    viewSwitcher.prepend(_worldBackBtn);
  }
}

function updateTopHud(state: GameStateData): void {
  dayDisplay.textContent = t('hud_operation_day', { n: state.dayNumber });

  // In-game clock
  const gameTime = getInGameTime(state);
  const clockEl = document.getElementById('game-clock');
  if (clockEl) {
    clockEl.textContent = formatInGameTime(gameTime);

    // Update clock style based on time
    if (gameTime.hour >= 21 || gameTime.hour < 6) {
      clockEl.className = 'game-clock clock-late';
    } else if (gameTime.hour >= 17) {
      clockEl.className = 'game-clock clock-evening';
    } else {
      clockEl.className = 'game-clock clock-day';
    }
  }


  // Day phase badge (day/night/rain)
  const phaseBadge = document.getElementById('day-phase-badge');
  if (phaseBadge) {
    const phase = state.dayPhase || 'day';
    phaseBadge.className = `phase-badge phase-${phase}`;
    phaseBadge.textContent = t(`time_phase_${phase}` as Parameters<typeof t>[0]);
  }
}

function toggleTopHudButtons(_state: GameStateData): void {
  // settings button removed from HUD
}

function renderContextualActions(state: GameStateData): void {
  const container = document.getElementById('building-container');
  if (!container) return;

  if (_dayPhaseActionsEl) {
    _dayPhaseActionsEl.remove();
    _dayPhaseActionsEl = null;
  }

  if (state.worldScreen === 'world') return;

  const phase = state.dayPhase || 'day';
  if (phase !== 'evening') return;

  _dayPhaseActionsEl = document.createElement('div');
  _dayPhaseActionsEl.className = 'day-phase-actions';

  const currentSpeed = state.gameSpeed || 1;
  const speedBtn = document.createElement('button');
  speedBtn.className = `day-phase-btn speed-btn${currentSpeed === 2 ? ' active' : ''}`;
  speedBtn.textContent = t('speed_btn', { n: currentSpeed === 1 ? 2 : 1 });
  speedBtn.addEventListener('click', () => {
    const newSpeed = currentSpeed === 1 ? 2 : 1;
    dispatch({ type: 'SET_GAME_SPEED', payload: { speed: newSpeed as 1 | 2 }, timestamp: Date.now() });
  });
  _dayPhaseActionsEl.appendChild(speedBtn);

  container.appendChild(_dayPhaseActionsEl);
}

function renderDayPhaseActions(state: GameStateData): void {
  renderContextualActions(state);
}

function processDayPhaseTransitions(state: GameStateData): void {
  // Check for night transition (evening time expired)
  if (consumeNightTransition()) {
    if (!_nightSummaryShown) {
      _nightSummaryShown = true;
      // Auto-transition to night
      dispatch({ type: 'ADVANCE_TO_NIGHT', payload: {}, timestamp: Date.now() });
      // Show day summary
      setTimeout(() => {
        showDaySummaryNew(gameState.get());
      }, 100);
    }
  }

  // Check for day auto-transition hint
  if (consumeDayAutoTransition()) {
    // no-op
  }

  // Reset night summary flag when we're back to day phase
  if (state.dayPhase === 'day' || state.dayPhase === 'evening') {
    _nightSummaryShown = false;
  }
}

function scheduleRender(state: GameStateData): void {
  lastRenderedState = state;
  if (renderScheduled) return;
  renderScheduled = true;
  requestAnimationFrame(() => {
    renderScheduled = false;
    if (!lastRenderedState) return;
    doRender(lastRenderedState);
  });
}

function doRender(state: GameStateData): void {
  spDisplay.textContent = formatSp(state.sp);
  fameDisplay.textContent = formatSp(state.fame);

  updateTopHud(state);
  toggleTopHudButtons(state);
  renderWorldBackButton(state);
  renderDayPhaseActions(state);
  processDayPhaseTransitions(state);

  if (state.worldScreen === 'world') {
    // If facilities fullscreen is active, keep it visible
    if (_facilitiesFullscreen) {
      worldScreen.style.display = 'none';
      facilitiesContainer.style.display = '';
      buildingContainer.style.display = 'none';
      lobbyContainer.style.display = 'none';
      academyContainer.style.display = 'none';
      document.getElementById('view-switcher')!.style.display = 'none';
      return;
    }
    worldScreen.style.display = '';
    buildingContainer.style.display = 'none';
    lobbyContainer.style.display = 'none';
    facilitiesContainer.style.display = 'none';
    academyContainer.style.display = 'none';
    document.getElementById('view-switcher')!.style.display = 'none';
    updateTabBar(true);
    renderWorldScreen(state);
    return;
  }
  updateTabBar(false);

  renderVisitorQueue(visitorQueueEl, state);
  renderBuilding(buildingEl, state, handleRoomClick);
  updateVisitors(buildingEl, state);

  const effectCanvas = document.getElementById('effect-canvas') as HTMLCanvasElement | null;
  if (effectLayerInitialized && effectCanvas) {
    const canvasRect = effectCanvas.getBoundingClientRect();
    if (canvasRect.width > 0 && canvasRect.height > 0) {
      let visitorFloorLevel = -1;
      if (state.isOpen && state.visitorGroup && !state.visitorGroup.done) {
        visitorFloorLevel = state.building[state.visitorGroup.currentFloorIndex]?.level ?? -1;
      }

      effectLayer.syncRoomEffects([], canvasRect, state.isOpen, visitorFloorLevel);

      if (state.visitorGroup && !state.visitorGroup.done) {
        const currentFloorIdx = state.visitorGroup.currentFloorIndex;
        if (currentFloorIdx !== prevVisitorFloorIndex) {
          const floorLevel = state.building[currentFloorIdx]?.level;
          if (floorLevel !== undefined) {
            effectLayer.triggerScareEffects(floorLevel);
          }
          playWhoosh();
          prevVisitorFloorIndex = currentFloorIdx;
        }
      } else {
        prevVisitorFloorIndex = -1;
      }
    }
  }

  worldScreen.style.display = 'none';
  document.getElementById('view-switcher')!.style.display = '';

  // Sync view container visibility with state
  const currentView = state.currentView ?? 'building';
  for (const [key, el] of Object.entries(viewContainers)) {
    el.style.display = key === currentView ? '' : 'none';
  }
  // Update view-switcher tab active states
  document.querySelectorAll<HTMLButtonElement>('.view-tab').forEach((btn) => {
    btn.classList.toggle('active', btn.dataset.view === currentView);
  });

  // Render active view content
  if (currentView === 'lobby') {
    renderLobbyView(lobbyContent, state, (ev) => {
      if (ev.action === 'build_floor_facility') {
        dispatch({ type: 'BUILD_LOBBY_FLOOR_FACILITY', payload: { level: ev.level, facilityType: ev.facilityType }, timestamp: Date.now() });
      } else if (ev.action === 'upgrade_floor') {
        dispatch({ type: 'UPGRADE_LOBBY_FLOOR', payload: { level: ev.level }, timestamp: Date.now() });
      } else if (ev.action === 'assign_ghost') {
        dispatch({ type: 'ASSIGN_LOBBY_GHOST', payload: { ghostInstanceId: ev.ghostInstanceId, floorLevel: ev.floorLevel }, timestamp: Date.now() });
      } else if (ev.action === 'unassign_ghost') {
        dispatch({ type: 'UNASSIGN_LOBBY_GHOST', payload: { floorLevel: ev.floorLevel }, timestamp: Date.now() });
      } else if (ev.action === 'buy_prop') {
        dispatch({ type: 'BUY_LOBBY_PROP', payload: { floorLevel: ev.floorLevel, propId: ev.propId }, timestamp: Date.now() });
      } else if (ev.action === 'add_floor') {
        dispatch({ type: 'ADD_LOBBY_FLOOR', payload: {}, timestamp: Date.now() });
      } else if (ev.action === 'assign_floor_npc') {
        dispatch({ type: 'ASSIGN_LOBBY_FLOOR_NPC', payload: { level: ev.level, ghostInstanceId: ev.ghostInstanceId }, timestamp: Date.now() });
      } else if (ev.action === 'unassign_floor_npc') {
        dispatch({ type: 'UNASSIGN_LOBBY_FLOOR_NPC', payload: { level: ev.level }, timestamp: Date.now() });
      } else if (ev.action === 'demolish_facility') {
        dispatch({ type: 'DEMOLISH_LOBBY_FACILITY_FLOOR', payload: { level: ev.level }, timestamp: Date.now() });
      } else if (ev.action === 'set_ticket_price') {
        dispatch({ type: 'SET_TICKET_PRICE', payload: { price: ev.price }, timestamp: Date.now() });
      }
    });
  } else if (currentView === 'facilities') {
    renderFacilitiesView(facilitiesContent, state, facilitiesActionHandler);
  } else if (currentView === 'academy') {
    renderAcademyView(academyContent, state, (ev) => {
      if (ev.action === 'start_training') {
        dispatch({ type: 'START_ACADEMY_TRAINING', payload: { ghostInstanceId: ev.ghostInstanceId, trainingId: ev.trainingId }, timestamp: Date.now() });
      } else if (ev.action === 'complete_training') {
        dispatch({ type: 'COMPLETE_ACADEMY_TRAINING', payload: { ghostInstanceId: ev.ghostInstanceId }, timestamp: Date.now() });
      } else if (ev.action === 'skip_training') {
        dispatch({ type: 'SKIP_ACADEMY_TRAINING', payload: { ghostInstanceId: ev.ghostInstanceId }, timestamp: Date.now() });
      }
    });
  }

  // Render scare popups (floating SP indicators)
  renderScarePopups(state);

  // Ghost speech bubbles (idle chatter)
  renderGhostSpeechBubbles(state);

  // Process reviews from completed visitor groups
  processReviews();

  // Process random events
  processRandomEvents();

  // Auto-complete training
  checkTrainingCompletions();

  // Auto-complete academy sessions
  checkAcademyCompletions();

  // Story arc guide popup (show once when 4+ built rooms)
  checkStoryArcGuide(state);
}

/* function openTabPopup(tab: string): void {
  if (tabPopupClosing) return;

  // Already open with same tab — close it (toggle)
  if (currentTab === tab && tabPopupOverlay) {
    closeTabPopup();
    return;
  }

  currentTab = tab;

  // Update tab bar active state
  document.querySelectorAll('.tab-btn').forEach((b) => b.classList.remove('active'));
  document.querySelector(`.tab-btn[data-tab="${tab}"]`)?.classList.add('active');

  // If popup already open, just swap content
  if (tabPopupOverlay && tabPopupSheet && tabPopupContentEl) {
    const titleEl = tabPopupSheet.querySelector('.tab-popup-title');
    if (titleEl) {
      const titleKey = TAB_TITLES[tab];
      titleEl.textContent = titleKey ? t(titleKey) : tab;
    }
    tabPopupContentEl.innerHTML = '';
    renderCurrentPanel(gameState.get());
    return;
  }

  // Create overlay
  tabPopupOverlay = document.createElement('div');
  tabPopupOverlay.className = 'tab-popup-overlay';
  tabPopupOverlay.addEventListener('click', (e) => {
    if (e.target === tabPopupOverlay) closeTabPopup();
  });

  // Create center popup (not bottom sheet)
  tabPopupSheet = document.createElement('div');
  tabPopupSheet.className = 'tab-popup-center';

  // Header
  const header = document.createElement('div');
  header.className = 'tab-popup-header';

  const titleEl = document.createElement('div');
  titleEl.className = 'tab-popup-title';
  const titleKey = TAB_TITLES[tab];
  titleEl.textContent = titleKey ? t(titleKey) : tab;
  header.appendChild(titleEl);

  const closeBtn = document.createElement('button');
  closeBtn.className = 'tab-popup-close-btn';
  closeBtn.textContent = 'X';
  closeBtn.setAttribute('aria-label', t('close'));
  closeBtn.addEventListener('click', () => closeTabPopup());
  header.appendChild(closeBtn);

  tabPopupSheet.appendChild(header);

  // Content area
  tabPopupContentEl = document.createElement('div');
  tabPopupContentEl.className = 'tab-popup-content';
  tabPopupSheet.appendChild(tabPopupContentEl);

  // No swipe handling needed for center popup
  // Mount to body (not game-ui) so position:fixed works correctly
  document.body.appendChild(tabPopupOverlay);
  document.body.appendChild(tabPopupSheet);

  // Trigger slide-up animation on next frame
  requestAnimationFrame(() => {
    tabPopupOverlay?.classList.add('visible');
    tabPopupSheet?.classList.add('visible');
  });

  // Render content
  renderCurrentPanel(gameState.get());
}
*/

function closeTabPopup(): void {
  if (tabPopupClosing) return;
  if (!tabPopupOverlay || !tabPopupSheet) return;

  tabPopupClosing = true;

  // Clear tab bar active state
  document.querySelectorAll('.tab-btn').forEach((b) => b.classList.remove('active'));
  currentTab = null;

  // Animate out
  tabPopupSheet.style.transform = '';
  tabPopupSheet.classList.remove('visible');
  tabPopupSheet.classList.add('closing');
  tabPopupOverlay.classList.remove('visible');
  tabPopupOverlay.classList.add('closing');

  const overlay = tabPopupOverlay;
  const sheet = tabPopupSheet;

  setTimeout(() => {
    overlay.remove();
    sheet.remove();
    tabPopupOverlay = null;
    tabPopupSheet = null;
    tabPopupContentEl = null;
    tabPopupClosing = false;
  }, 300);
}

document.querySelectorAll<HTMLButtonElement>('.tab-btn').forEach((btn) => {
  btn.addEventListener('click', () => {
    const tab = btn.dataset.tab;
    if (!tab) return;
    openTabPopup(tab);
  });
});

// === View Switcher ===
const viewContainers: Record<MainView, HTMLElement> = {
  building: buildingContainer,
  lobby: lobbyContainer,
  facilities: facilitiesContainer,
  academy: academyContainer,
};

function switchView(view: MainView): void {
  // Hide all view containers
  for (const [key, el] of Object.entries(viewContainers)) {
    el.style.display = key === view ? '' : 'none';
  }
  // Update tab buttons
  document.querySelectorAll<HTMLButtonElement>('.view-tab').forEach((btn) => {
    btn.classList.toggle('active', btn.dataset.view === view);
  });
  // Dispatch state change
  dispatch({ type: 'SWITCH_VIEW', payload: { view }, timestamp: Date.now() });
}

document.querySelectorAll<HTMLButtonElement>('.view-tab').forEach((btn) => {
  btn.addEventListener('click', () => {
    const view = btn.dataset.view as MainView;
    if (view) switchView(view);
  });
});

// Event delegation fallback for world-back-btn (ensures clicks register on all screen sizes)
document.getElementById('view-switcher')?.addEventListener('click', (e) => {
  const target = (e.target as HTMLElement).closest('#world-back-btn');
  if (target) {
    e.stopPropagation();
    dispatch({ type: 'SET_WORLD_SCREEN', payload: { screen: 'world' }, timestamp: Date.now() });
  }
});

// === Tutorial System ===
// Steps: 0=not started, 1=welcome, 2=build room, 3=assign ghost, 4=open house, 99=completed
let _tutorialPopupOpen = false;

// @ts-expect-error legacy tutorial system kept for reference
function showTutorialPopup(step: number): void {
  if (_tutorialPopupOpen) return;
  _tutorialPopupOpen = true;

  const overlay = document.createElement('div');
  overlay.className = 'popup-overlay tutorial-overlay';
  overlay.id = 'tutorial-popup';

  const box = document.createElement('div');
  box.className = 'popup-box tutorial-box';

  let titleKey = '';
  let bodyKey = '';
  let btnKey = '';
  let showSkip = true;

  switch (step) {
    case 1:
      titleKey = 'tutorial_welcome_title';
      bodyKey = 'tutorial_welcome_body';
      btnKey = 'tutorial_welcome_btn';
      break;
    case 2:
      titleKey = 'tutorial_build_title';
      bodyKey = 'tutorial_build_body';
      btnKey = 'tutorial_next';
      break;
    case 3:
      titleKey = 'tutorial_ghost_title';
      bodyKey = 'tutorial_ghost_body';
      btnKey = 'tutorial_next';
      break;
    case 4:
      titleKey = 'tutorial_open_title';
      bodyKey = 'tutorial_open_body';
      btnKey = 'tutorial_next';
      break;
    case 5:
      titleKey = 'tutorial_facility_intro_title';
      bodyKey = 'tutorial_facility_intro_body';
      btnKey = 'tutorial_next';
      break;
    case 6:
      titleKey = 'tutorial_facility_build_title';
      bodyKey = 'tutorial_facility_build_body';
      btnKey = 'tutorial_next';
      break;
    case 7:
      titleKey = 'tutorial_facility_staff_title';
      bodyKey = 'tutorial_facility_staff_body';
      btnKey = 'tutorial_next';
      break;
    case 8:
      titleKey = 'tutorial_facility_open_title';
      bodyKey = 'tutorial_facility_open_body';
      btnKey = 'tutorial_next';
      break;
    case 99:
      titleKey = 'tutorial_done_title';
      bodyKey = 'tutorial_done_body';
      btnKey = 'tutorial_got_it';
      showSkip = false;
      break;
    default:
      _tutorialPopupOpen = false;
      return;
  }

  const title = document.createElement('h2');
  title.className = 'tutorial-title';
  title.textContent = t(titleKey as any);
  box.appendChild(title);

  const body = document.createElement('p');
  body.className = 'tutorial-body';
  body.textContent = t(bodyKey as any);
  box.appendChild(body);

  const btnRow = document.createElement('div');
  btnRow.className = 'tutorial-btn-row';

  if (showSkip) {
    const skipBtn = document.createElement('button');
    skipBtn.className = 'tutorial-skip-btn';
    skipBtn.textContent = t('tutorial_skip');
    skipBtn.addEventListener('click', () => {
      dispatch({ type: 'ADVANCE_TUTORIAL', payload: { step: 99 }, timestamp: Date.now() });
      _tutorialPopupOpen = false;
      overlay.remove();
    });
    btnRow.appendChild(skipBtn);
  }

  const nextBtn = document.createElement('button');
  nextBtn.className = 'tutorial-next-btn';
  nextBtn.textContent = t(btnKey as any);
  nextBtn.addEventListener('click', () => {
    _tutorialPopupOpen = false;
    overlay.remove();
    if (step === 1) {
      // After welcome, advance to step 2 (waiting for build room)
      dispatch({ type: 'ADVANCE_TUTORIAL', payload: { step: 2 }, timestamp: Date.now() });
      setTimeout(() => showTutorialPopup(2), 300);
    } else if (step === 5) {
      // Facility intro → advance to step 6 (waiting for BUILD_FACILITY_ON_PLOT)
      dispatch({ type: 'ADVANCE_TUTORIAL', payload: { step: 6 }, timestamp: Date.now() });
      setTimeout(() => showTutorialPopup(6), 300);
    } else if (step === 99) {
      // Tutorial complete, nothing more
    }
    // For steps 2-4, 6-8: popup dismissed, user does the action, then next step triggers
  });
  btnRow.appendChild(nextBtn);

  box.appendChild(btnRow);
  overlay.appendChild(box);
  document.getElementById('app')!.appendChild(overlay);
}

function showComicIntro(): void {
  const app = document.getElementById('app')!;
  const overlay = document.createElement('div');
  overlay.className = 'comic-intro-overlay';

  const panels = [
    '/images/comic/panel-1.webp',
    '/images/comic/panel-2.webp',
    '/images/comic/panel-3.webp',
    '/images/comic/panel-4.webp',
    '/images/comic/panel-5.webp',
    '/images/comic/panel-6.webp',
  ];

  let currentPanel = 0;

  const container = document.createElement('div');
  container.className = 'comic-intro-container';

  const img = document.createElement('img');
  img.className = 'comic-intro-img';
  img.src = panels[0];
  img.alt = '';

  const dots = document.createElement('div');
  dots.className = 'comic-intro-dots';
  for (let i = 0; i < panels.length; i++) {
    const dot = document.createElement('span');
    dot.className = 'comic-intro-dot' + (i === 0 ? ' active' : '');
    dots.appendChild(dot);
  }

  const skipBtn = document.createElement('button');
  skipBtn.className = 'comic-intro-skip';
  skipBtn.textContent = 'Skip';
  skipBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    finishIntro();
  });

  container.appendChild(img);
  container.appendChild(dots);
  container.appendChild(skipBtn);
  overlay.appendChild(container);
  app.appendChild(overlay);

  // Fade in
  requestAnimationFrame(() => overlay.classList.add('visible'));

  let transitioning = false;

  function advancePanel(): void {
    if (transitioning) return;
    currentPanel++;
    if (currentPanel >= panels.length) {
      finishIntro();
      return;
    }
    transitioning = true;
    img.classList.add('comic-intro-fade');
    setTimeout(() => {
      img.src = panels[currentPanel];
      img.onload = () => {
        img.classList.remove('comic-intro-fade');
        transitioning = false;
      };
      // Fallback if onload doesn't fire (cached)
      setTimeout(() => {
        img.classList.remove('comic-intro-fade');
        transitioning = false;
      }, 100);
      // Update dots
      const allDots = dots.querySelectorAll('.comic-intro-dot');
      allDots.forEach((d, i) => d.classList.toggle('active', i === currentPanel));
    }, 150);
  }

  function finishIntro(): void {
    overlay.classList.remove('visible');
    setTimeout(() => {
      overlay.remove();
      dispatch({ type: 'ADVANCE_TUTORIAL', payload: { step: 99 }, timestamp: Date.now() });
      // After intro, go to building view (room management)
      dispatch({ type: 'SET_WORLD_SCREEN', payload: { screen: 'haunted_building' }, timestamp: Date.now() });
      dispatch({ type: 'SWITCH_VIEW', payload: { view: 'building' }, timestamp: Date.now() });
      closeFacilitiesFullscreen();
    }, 300);
  }

  // Tap anywhere on overlay or container to advance
  overlay.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).classList.contains('comic-intro-skip')) return;
    advancePanel();
  });
}

function checkTutorialProgress(): void {
  const s = gameState.get();
  if (s.tutorialStep >= 99) return;

  // Step 0: first time → show comic intro instead of tutorial
  if (s.tutorialStep === 0) {
    setTimeout(() => showComicIntro(), 500);
    return;
  }
  // Any in-progress tutorial step → skip to done
  if (s.tutorialStep > 0 && s.tutorialStep < 99) {
    dispatch({ type: 'ADVANCE_TUTORIAL', payload: { step: 99 }, timestamp: Date.now() });
  }
}

// === Dispatch Command ===
function dispatch(cmd: GameCommand): void {
  const recorded = recorder.record(cmd);
  const current = syncSelectedBuildingToState(gameState.get());
  const executed = executor.execute(recorded, current);
  // Sync building changes back to hauntedBuildings before mapping
  const synced = syncSelectedBuildingToState(executed);
  gameState.set(mapStateToSelectedBuilding(synced));

  // SFX triggers for key actions
  if (cmd.type === 'BUILD_ROOM' || cmd.type === 'HIRE_GHOST' || cmd.type === 'BUY_PROP') {
    playCoin();
  } else if (cmd.type === 'TOGGLE_OPEN') {
    playPop();
  }

  // Tutorial progression removed — comic intro handles onboarding
}

// === Dev Cheats (console only) ===
(window as any).__cheat = {
  addSp: (amount = 100000) => {
    const s = gameState.get();
    gameState.set({ ...s, sp: s.sp + amount, totalSpEarned: s.totalSpEarned + amount });
  },
  open: () => {
    dispatch({ type: 'START_EVENING', payload: {}, timestamp: Date.now() });
  },
  night: () => {
    dispatch({ type: 'ADVANCE_TO_NIGHT', payload: {}, timestamp: Date.now() });
  },
  nextDay: () => {
    dispatch({ type: 'ADVANCE_TO_DAY', payload: {}, timestamp: Date.now() });
  },
  maxStats: () => {
    const s = gameState.get();
    gameState.set({ ...s, fame: 1000, starLevel: 25, starEvolution: 4, reputation: 100 });
  },
  addProp: (floor = 1, propId = 'dry_ice') => {
    const s = gameState.get();
    const f = s.building.find(f => f.level === floor);
    if (f?.room) {
      const exists = f.room.props.some(p => p.propId === propId);
      if (!exists) {
        f.room.props.push({ propId: propId as any, level: 1 });
        gameState.set({ ...s });
      }
    }
  },
  reset: () => { gameState.reset(); location.reload(); },
  buildRoom: (floorLevel = 1, roomType = 'dark_corridor' as import('./game/data/rooms.js').RoomTypeId) => {
    dispatch({ type: 'BUILD_ROOM', payload: { floorLevel, roomType }, timestamp: Date.now() });
  },
  hireGhost: (ghostCharacterId: string) => {
    dispatch({ type: 'HIRE_GHOST', payload: { ghostCharacterId: ghostCharacterId as import('./game/data/ghosts.js').GhostId }, timestamp: Date.now() });
  },
  dispatch: (cmd: any) => dispatch(cmd),
  getState: () => gameState.get(),
  setState: (patch: Partial<GameStateData>) => { const s = gameState.get(); gameState.set({ ...s, ...patch }); },
};

// === Room Click Handling ===
function handleRoomClick(floorLevel: number, room: import('./game/state.js').Room | null): void {
  const state = gameState.get();

  if (room === null) {
    showRoomTypePicker(floorLevel, state);
  } else {
    showRoomDetail(room, floorLevel, state);
  }
}

function showRoomTypePicker(floorLevel: number, state: GameStateData): void {
  const overlay = document.createElement('div');
  overlay.className = 'popup-overlay';
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });

  const box = document.createElement('div');
  box.className = 'popup-box';

  const title = document.createElement('div');
  title.className = 'popup-title';
  title.textContent = t('build_room');
  box.appendChild(title);

  const list = document.createElement('div');
  list.className = 'room-type-list';

  // Filter rooms by current building's theme
  const currentThemeId = getSelectedHauntedBuilding(state).themeId;
  const filteredRoomIds = currentThemeId
    ? ALL_ROOM_TYPE_IDS.filter(id => ROOM_TYPES[id].themeTags.includes(currentThemeId))
    : ALL_ROOM_TYPE_IDS;

  for (const roomTypeId of filteredRoomIds) {
    const def = ROOM_TYPES[roomTypeId];
    const isUnlocked = state.unlockedRoomTypes.includes(roomTypeId);

    const option = document.createElement('div');
    option.className = 'room-type-option';
    if (!isUnlocked) {
      option.classList.add('room-type-locked');
    }

    // Preview image
    const imgWrapper = document.createElement('div');
    imgWrapper.className = 'room-type-img-wrapper';
    const img = document.createElement('img');
    img.className = 'room-type-img';
    img.src = def.imagePath;
    img.alt = t(def.nameKey as Parameters<typeof t>[0]);
    img.draggable = false;
    imgWrapper.appendChild(img);
    option.appendChild(imgWrapper);

    // Text info
    const textWrapper = document.createElement('div');
    textWrapper.className = 'room-type-text';

    const nameEl = document.createElement('div');
    nameEl.className = 'room-type-name';
    nameEl.textContent = t(def.nameKey as Parameters<typeof t>[0]);
    textWrapper.appendChild(nameEl);

    const descEl = document.createElement('div');
    descEl.className = 'room-type-desc';
    if (isUnlocked) {
      descEl.textContent = t(def.descKey as Parameters<typeof t>[0]);
    } else {
      descEl.textContent = t('room_unlock_in_shop');
    }
    textWrapper.appendChild(descEl);

    // Available props preview
    if (def.availableProps && def.availableProps.length > 0) {
      const propsPreview = document.createElement('div');
      propsPreview.style.cssText = 'font-size:9px;color:#c084fc;margin-top:2px;';
      const propNames = def.availableProps
        .map((pid) => {
          const pDef = PROP_DEFS[pid];
          return pDef ? t(pDef.nameKey as Parameters<typeof t>[0]) : '';
        })
        .filter(Boolean);
      propsPreview.textContent = t('room_available_props', { props: propNames.join(', ') });
      textWrapper.appendChild(propsPreview);
    }

    option.appendChild(textWrapper);

    if (isUnlocked) {
      option.addEventListener('click', () => {
        dispatch({ type: 'BUILD_ROOM', payload: { floorLevel, roomType: roomTypeId }, timestamp: Date.now() });
        overlay.remove();
      });
    }

    list.appendChild(option);
  }

  box.appendChild(list);

  const cancelBtn = document.createElement('button');
  cancelBtn.className = 'popup-btn secondary';
  cancelBtn.style.cssText = 'width:100%;margin-top:12px;min-height:48px;';
  cancelBtn.textContent = t('cancel');
  cancelBtn.addEventListener('click', () => overlay.remove());
  box.appendChild(cancelBtn);

  overlay.appendChild(box);
  popupContainer.appendChild(overlay);
}

function showRoomDetail(room: import('./game/state.js').Room, floorLevel: number, state: GameStateData): void {
  const overlay = document.createElement('div');
  overlay.className = 'popup-overlay';
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });

  const box = document.createElement('div');
  box.className = 'popup-box room-detail-popup';

  const roomDef = ROOM_TYPES[room.type];

  // Header: room image banner + name overlay
  const headerEl = document.createElement('div');
  headerEl.className = 'room-detail-header';
  const headerImg = document.createElement('img');
  headerImg.className = 'room-detail-header-img';
  headerImg.src = roomDef.imagePath;
  headerImg.alt = '';
  headerImg.draggable = false;
  headerEl.appendChild(headerImg);
  const headerName = document.createElement('div');
  headerName.className = 'room-detail-header-name';
  headerName.textContent = t(roomDef.nameKey as Parameters<typeof t>[0]);
  if (room.upgradeLevel > 1) headerName.textContent += ` +${room.upgradeLevel - 1}`;
  headerEl.appendChild(headerName);
  box.appendChild(headerEl);

  // Tab bar
  const tabBar = document.createElement('div');
  tabBar.className = 'room-detail-tabs';
  const tabIds = ['info', 'props', 'upgrade', 'demolish'] as const;
  const tabKeys = [
    'room_detail_tab_info',
    'room_detail_tab_props',
    'room_detail_tab_upgrade',
    'room_detail_tab_demolish',
  ] as const;

  const contentArea = document.createElement('div');
  contentArea.className = 'room-detail-content';

  function switchTab(tabId: string): void {
    tabBar.querySelectorAll('.room-detail-tab').forEach((btn) => btn.classList.remove('active'));
    tabBar.querySelector(`[data-tab="${tabId}"]`)?.classList.add('active');
    contentArea.innerHTML = '';
    if (tabId === 'info') renderInfoTab(contentArea, room, floorLevel, state, overlay);
    else if (tabId === 'props') renderPropsTab(contentArea, room, state, overlay);
    else if (tabId === 'upgrade') renderUpgradeTab(contentArea, room, floorLevel, state, overlay);
    else if (tabId === 'demolish') renderDemolishTab(contentArea, floorLevel, overlay);
  }

  tabIds.forEach((id, i) => {
    const tabBtn = document.createElement('button');
    tabBtn.className = 'room-detail-tab';
    tabBtn.dataset.tab = id;
    tabBtn.textContent = t(tabKeys[i] as Parameters<typeof t>[0]);
    if (i === 0) tabBtn.classList.add('active');
    tabBtn.addEventListener('click', () => switchTab(id));
    tabBar.appendChild(tabBtn);
  });

  box.appendChild(tabBar);
  box.appendChild(contentArea);

  // Close button (inside popup box, flex-shrink:0 to stay visible)
  const closeBtn = document.createElement('button');
  closeBtn.className = 'popup-btn secondary';
  closeBtn.style.cssText = 'min-height:44px;flex-shrink:0;';
  closeBtn.textContent = t('close');
  closeBtn.addEventListener('click', () => overlay.remove());
  box.appendChild(closeBtn);

  overlay.appendChild(box);
  popupContainer.appendChild(overlay);

  // Render initial tab
  switchTab('info');
}

// --- Room Detail: Info Tab ---
function renderInfoTab(
  container: HTMLElement,
  room: import('./game/state.js').Room,
  floorLevel: number,
  state: GameStateData,
  overlay: HTMLElement,
): void {
  const roomDef = ROOM_TYPES[room.type];

  // Room description
  const descEl = document.createElement('div');
  descEl.className = 'room-detail-desc';
  descEl.textContent = t(roomDef.descKey as Parameters<typeof t>[0]);
  container.appendChild(descEl);

  // Room emotion stats
  const statsSection = document.createElement('div');
  statsSection.className = 'room-detail-section';
  const statsLabel = document.createElement('div');
  statsLabel.className = 'room-detail-section-label';
  statsLabel.textContent = t('stat_total' as Parameters<typeof t>[0]);
  statsSection.appendChild(statsLabel);

  const roomStats = calcRoomStats(room, state);
  const roomGrade = calcRoomGrade(roomStats);

  const statDefs: Array<{ key: string; value: number; color: string }> = [
    { key: 'stat_fear', value: roomStats.fear, color: '#ef4444' },
    { key: 'stat_thrill', value: roomStats.thrill, color: '#f97316' },
    { key: 'stat_atmosphere', value: roomStats.atmosphere, color: '#a855f7' },
    { key: 'stat_fun', value: roomStats.fun, color: '#eab308' },
  ];

  for (const sd of statDefs) {
    const statRow = document.createElement('div');
    statRow.style.cssText = 'display:flex;align-items:center;gap:8px;margin-bottom:4px;';

    const statName = document.createElement('span');
    statName.style.cssText = 'font-size:11px;color:#c0b8d8;min-width:48px;text-align:right;';
    statName.textContent = t(sd.key as Parameters<typeof t>[0]);
    statRow.appendChild(statName);

    const barOuter = document.createElement('div');
    barOuter.style.cssText = 'flex:1;height:10px;background:#1a1a2e;border-radius:5px;overflow:hidden;';
    const barFill = document.createElement('div');
    barFill.style.cssText = `width:${sd.value}%;height:100%;background:${sd.color};border-radius:5px;transition:width 0.3s;`;
    barOuter.appendChild(barFill);
    statRow.appendChild(barOuter);

    const statValue = document.createElement('span');
    statValue.style.cssText = 'font-size:11px;color:#f0e8ff;min-width:24px;text-align:right;font-weight:600;';
    statValue.textContent = String(sd.value);
    statRow.appendChild(statValue);

    statsSection.appendChild(statRow);
  }

  // Grade badge
  const gradeColors: Record<RoomGrade, string> = {
    S: '#fbbf24', A: '#a855f7', B: '#3b82f6', C: '#22c55e', D: '#eab308', F: '#ef4444',
  };
  const gradeBadge = document.createElement('div');
  gradeBadge.style.cssText = `display:inline-flex;align-items:center;gap:6px;margin-top:6px;padding:4px 12px;border-radius:8px;background:${gradeColors[roomGrade]}22;border:1px solid ${gradeColors[roomGrade]}66;`;
  const gradeLabel = document.createElement('span');
  gradeLabel.style.cssText = 'font-size:11px;color:#c0b8d8;';
  gradeLabel.textContent = t('room_grade' as Parameters<typeof t>[0]);
  gradeBadge.appendChild(gradeLabel);
  const gradeValue = document.createElement('span');
  gradeValue.style.cssText = `font-size:16px;font-weight:bold;color:${gradeColors[roomGrade]};`;
  gradeValue.textContent = roomGrade;
  gradeBadge.appendChild(gradeValue);
  statsSection.appendChild(gradeBadge);

  container.appendChild(statsSection);

  // Assigned ghost
  const ghostSection = document.createElement('div');
  ghostSection.className = 'room-detail-section';
  const ghostLabel = document.createElement('div');
  ghostLabel.className = 'room-detail-section-label';
  ghostLabel.textContent = t('assign_ghost');
  ghostSection.appendChild(ghostLabel);

  const ghost = room.assignedGhostId
    ? state.ghosts.find((g) => g.id === room.assignedGhostId) ?? null
    : null;

  if (ghost) {
    const gDef = GHOST_DEFS[ghost.characterId];
    const ghostRow = document.createElement('div');
    ghostRow.className = 'room-detail-ghost-row';
    const ghostImg = document.createElement('img');
    ghostImg.className = 'room-detail-ghost-img';
    ghostImg.src = gDef.image;
    ghostImg.alt = '';
    ghostImg.draggable = false;
    ghostRow.appendChild(ghostImg);
    const ghostName = document.createElement('span');
    ghostName.className = 'room-detail-ghost-name';
    ghostName.textContent = `${t(gDef.nameKey as Parameters<typeof t>[0])} Lv.${ghost.level}`;
    ghostRow.appendChild(ghostName);
    ghostSection.appendChild(ghostRow);

    // Unassign button
    const unassignBtn = document.createElement('button');
    unassignBtn.className = 'popup-btn secondary';
    unassignBtn.style.cssText = 'width:100%;margin-top:6px;min-height:40px;font-size:12px;';
    unassignBtn.textContent = t('unassign_ghost');
    unassignBtn.addEventListener('click', () => {
      dispatch({ type: 'UNASSIGN_GHOST', payload: { ghostInstanceId: ghost.id }, timestamp: Date.now() });
      overlay.remove();
    });
    ghostSection.appendChild(unassignBtn);
  } else {
    const noGhostEl = document.createElement('div');
    noGhostEl.className = 'room-detail-no-ghost';
    noGhostEl.textContent = t('room_detail_ghost_none');
    ghostSection.appendChild(noGhostEl);

    // Assign ghost buttons
    const unassignedGhosts = state.ghosts.filter((g) => !g.assignedRoomId && !g.training && !g.academySession);
    for (const g of unassignedGhosts) {
      const gDef = GHOST_DEFS[g.characterId];
      const gBtn = document.createElement('button');
      gBtn.className = 'popup-btn secondary';
      gBtn.style.cssText = 'width:100%;margin-top:4px;min-height:40px;display:flex;align-items:center;gap:8px;font-size:12px;';
      const gBtnImg = document.createElement('img');
      gBtnImg.className = 'popup-assign-ghost-img';
      gBtnImg.src = gDef.image;
      gBtnImg.alt = '';
      gBtnImg.draggable = false;
      gBtn.appendChild(gBtnImg);
      const gBtnText = document.createElement('span');
      gBtnText.textContent = `${t(gDef.nameKey as Parameters<typeof t>[0])} (${gDef.grade}) Lv.${g.level}`;
      gBtn.appendChild(gBtnText);
      gBtn.addEventListener('click', () => {
        dispatch({ type: 'ASSIGN_GHOST', payload: { ghostInstanceId: g.id, roomId: room.id }, timestamp: Date.now() });
        overlay.remove();
      });
      ghostSection.appendChild(gBtn);
    }
  }
  container.appendChild(ghostSection);

  // Condition gauge
  const condSection = document.createElement('div');
  condSection.className = 'room-detail-section';
  const condLabel = document.createElement('div');
  condLabel.className = 'room-detail-section-label';
  condLabel.textContent = t('room_detail_condition');
  condSection.appendChild(condLabel);
  const condValue = document.createElement('span');
  condValue.className = 'room-detail-value';
  condValue.textContent = `${Math.round(room.condition)}/100`;
  if (room.condition > 60) condValue.style.color = '#34d399';
  else if (room.condition > 30) condValue.style.color = '#fbbf24';
  else condValue.style.color = '#ef4444';
  condSection.appendChild(condValue);
  // Collapsed warning
  if (room.collapsed) {
    const collapsedEl = document.createElement('div');
    collapsedEl.className = 'room-detail-warning';
    collapsedEl.textContent = t('room_detail_collapsed_desc');
    condSection.appendChild(collapsedEl);
  }

  // Repair button
  const repairCostBase = 200;
  const hasSturdyBonus = (room.conditionUpgrade ?? 0) >= 10;
  const repairCost = hasSturdyBonus ? Math.round(repairCostBase * 0.7) : repairCostBase;

  if (room.underRepair) {
    const remaining = Math.max(0, Math.ceil(((room.repairEndsAt ?? 0) - Date.now()) / 1000));
    const repairingEl = document.createElement('div');
    repairingEl.className = 'room-detail-status-active';
    repairingEl.textContent = t('room_detail_repairing', { sec: remaining });
    condSection.appendChild(repairingEl);
  } else if (room.condition <= 80 || room.collapsed) {
    const repairBtn = document.createElement('button');
    repairBtn.className = 'popup-btn primary';
    repairBtn.style.cssText = 'width:100%;margin-top:6px;min-height:40px;font-size:12px;';
    repairBtn.disabled = state.sp < repairCost || room.underCleaning;
    repairBtn.textContent = t('room_detail_request_repair', { sp: formatSp(repairCost) });
    repairBtn.addEventListener('click', () => {
      dispatch({ type: 'REQUEST_REPAIR', payload: { floorLevel }, timestamp: Date.now() });
      overlay.remove();
    });
    condSection.appendChild(repairBtn);
  } else {
    const condOk = document.createElement('div');
    condOk.className = 'room-detail-status-ok';
    condOk.textContent = t('room_detail_condition_ok');
    condSection.appendChild(condOk);
  }
  container.appendChild(condSection);

  // Hygiene gauge
  const hygSection = document.createElement('div');
  hygSection.className = 'room-detail-section';
  const hygLabel = document.createElement('div');
  hygLabel.className = 'room-detail-section-label';
  hygLabel.textContent = t('room_detail_hygiene');
  hygSection.appendChild(hygLabel);
  const hygValue = document.createElement('span');
  hygValue.className = 'room-detail-value';
  hygValue.textContent = `${Math.round(room.hygiene)}/100`;
  if (room.hygiene > 60) hygValue.style.color = '#34d399';
  else if (room.hygiene > 30) hygValue.style.color = '#fbbf24';
  else hygValue.style.color = '#ef4444';
  hygSection.appendChild(hygValue);

  if (room.underCleaning) {
    const remaining = Math.max(0, Math.ceil(((room.cleaningEndsAt ?? 0) - Date.now()) / 1000));
    const cleaningEl = document.createElement('div');
    cleaningEl.className = 'room-detail-status-active';
    cleaningEl.textContent = t('room_detail_cleaning', { sec: remaining });
    hygSection.appendChild(cleaningEl);
  } else if (room.hygiene <= 80) {
    const cleanBtn = document.createElement('button');
    cleanBtn.className = 'popup-btn primary';
    cleanBtn.style.cssText = 'width:100%;margin-top:6px;min-height:40px;font-size:12px;';
    cleanBtn.disabled = state.sp < 100 || room.underRepair;
    cleanBtn.textContent = t('room_detail_request_cleaning', { sp: formatSp(100) });
    cleanBtn.addEventListener('click', () => {
      dispatch({ type: 'REQUEST_CLEANING', payload: { floorLevel }, timestamp: Date.now() });
      overlay.remove();
    });
    hygSection.appendChild(cleanBtn);
  } else {
    const hygOk = document.createElement('div');
    hygOk.className = 'room-detail-status-ok';
    hygOk.textContent = t('room_detail_hygiene_ok');
    hygSection.appendChild(hygOk);
  }
  container.appendChild(hygSection);

  // Room level upgrade
  if (room.upgradeLevel < 3) {
    const toLevel = (room.upgradeLevel + 1) as 2 | 3;
    const base = CONFIG.floorCosts[Math.min(floorLevel, CONFIG.floorCosts.length - 1)] ?? 0;
    const fraction = toLevel === 2 ? CONFIG.roomUpgrade2Fraction : CONFIG.roomUpgrade3Fraction;
    const cost = Math.max(50, Math.floor(base * fraction));

    const upgradeBtn = document.createElement('button');
    upgradeBtn.className = 'popup-btn primary';
    upgradeBtn.style.cssText = 'width:100%;margin-top:10px;min-height:44px;';
    upgradeBtn.disabled = state.sp < cost;
    upgradeBtn.textContent = t('upgrade_room_cost', { sp: formatSp(cost) });
    upgradeBtn.addEventListener('click', () => {
      dispatch({ type: 'UPGRADE_ROOM', payload: { roomId: room.id }, timestamp: Date.now() });
      overlay.remove();
    });
    container.appendChild(upgradeBtn);
  }
}

// --- Room Detail: Props Tab ---
function renderPropsTab(
  container: HTMLElement,
  room: import('./game/state.js').Room,
  state: GameStateData,
  overlay: HTMLElement,
): void {
  // Helper: render a single prop item row
  function renderPropItem(propId: PropId): HTMLElement {
    const propDef = PROP_DEFS[propId];
    const existing = room.props.find((p) => p.propId === propId);
    const currentLevel = existing ? existing.level : 0;
    const nextLevel = currentLevel + 1;
    const isMaxed = nextLevel > propDef.maxLevel;
    const isInstalled = currentLevel > 0;

    const propRow = document.createElement('div');
    propRow.style.cssText = `display:flex;align-items:center;padding:6px 0;border-bottom:1px solid #2a2a4a;gap:8px;${isInstalled ? 'border-left:3px solid #34d399;padding-left:8px;' : ''}`;

    // Icon image
    const iconImg = document.createElement('img');
    iconImg.src = propDef.imagePath;
    iconImg.alt = '';
    iconImg.draggable = false;
    iconImg.style.cssText = 'width:36px;height:36px;object-fit:contain;flex-shrink:0;pointer-events:none;border-radius:6px;background:rgba(30,30,60,0.6);';
    propRow.appendChild(iconImg);

    const propInfo = document.createElement('div');
    propInfo.style.cssText = 'flex:1;min-width:0;';
    const propName = document.createElement('div');
    propName.style.cssText = 'font-size:11px;font-weight:600;color:#f0e8ff;';
    propName.textContent = t(propDef.nameKey as Parameters<typeof t>[0]);
    if (currentLevel > 0) {
      propName.textContent += ` ${t('prop_level', { n: currentLevel })}`;
    }
    propInfo.appendChild(propName);
    const propDesc = document.createElement('div');
    propDesc.style.cssText = 'font-size:9px;color:#a08ccc;';
    propDesc.textContent = t(propDef.descKey as Parameters<typeof t>[0]);
    propInfo.appendChild(propDesc);
    // Show bonus info
    const bonusEl = document.createElement('div');
    bonusEl.style.cssText = 'font-size:9px;color:#c084fc;margin-top:1px;';
    bonusEl.textContent = t('prop_bonus', { pct: Math.round(propDef.scareBonus * 100) });
    propInfo.appendChild(bonusEl);
    propRow.appendChild(propInfo);

    const propBtn = document.createElement('button');
    propBtn.style.cssText = 'padding:4px 10px;border-radius:6px;border:none;font-size:10px;font-weight:600;cursor:pointer;white-space:nowrap;min-height:32px;';

    if (isMaxed) {
      propBtn.style.background = '#2a2a4a';
      propBtn.style.color = '#34d399';
      propBtn.disabled = true;
      propBtn.textContent = t('prop_max');
    } else {
      const cost = propCostForLevel(propId, nextLevel);
      propBtn.style.background = 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)';
      propBtn.style.color = '#fff';
      propBtn.disabled = state.sp < cost;
      if (propBtn.disabled) {
        propBtn.style.background = '#2a2a4a';
        propBtn.style.color = '#a08ccc';
      }
      propBtn.textContent = currentLevel === 0
        ? t('prop_buy', { sp: formatSp(cost) })
        : t('prop_upgrade', { sp: formatSp(cost) });
      propBtn.addEventListener('click', () => {
        dispatch({ type: 'BUY_PROP', payload: { roomId: room.id, propId: propId as PropId }, timestamp: Date.now() });
        overlay.remove();
      });
    }

    propRow.appendChild(propBtn);
    return propRow;
  }

  // Filter props by room type's availableProps
  const roomDef = ROOM_TYPES[room.type];
  const available = roomDef.availableProps ?? [];
  const availableDecoProps = DECORATION_PROP_IDS.filter((id) => available.includes(id));
  const availableStagingProps = STAGING_PROP_IDS.filter((id) => available.includes(id));

  // --- Decoration Props Section ---
  if (availableDecoProps.length > 0) {
    const decoLabel = document.createElement('div');
    decoLabel.className = 'room-detail-section-label';
    decoLabel.textContent = t('props_category_decoration');
    container.appendChild(decoLabel);

    for (const propId of availableDecoProps) {
      container.appendChild(renderPropItem(propId));
    }
  }

  // --- Staging Equipment Section ---
  if (availableStagingProps.length > 0) {
    const stagingLabel = document.createElement('div');
    stagingLabel.className = 'room-detail-section-label';
    stagingLabel.style.marginTop = '16px';
    stagingLabel.textContent = t('props_category_staging');
    container.appendChild(stagingLabel);

    const stagingDesc = document.createElement('div');
    stagingDesc.style.cssText = 'font-size:10px;color:#a08ccc;margin-bottom:8px;';
    stagingDesc.textContent = t('props_staging_desc');
    container.appendChild(stagingDesc);

    for (const propId of availableStagingProps) {
      container.appendChild(renderPropItem(propId));
    }
  }
}

// --- Room Detail: Upgrade Tab ---
function renderUpgradeTab(
  container: HTMLElement,
  room: import('./game/state.js').Room,
  floorLevel: number,
  state: GameStateData,
  overlay: HTMLElement,
): void {
  // Structural Reinforcement
  const condLevel = room.conditionUpgrade ?? 0;
  container.appendChild(createUpgradeSection(
    t('room_upgrade_structure'),
    t('room_upgrade_structure_desc'),
    t('room_upgrade_structure_effect', { pct: 5 }),
    condLevel,
    20,
    Math.round(500 * Math.pow(1.5, condLevel)),
    state.sp,
    [
      { level: 10, text: t('room_upgrade_structure_bonus_10'), unlocked: condLevel >= 10 },
      { level: 20, text: t('room_upgrade_structure_bonus_20'), unlocked: condLevel >= 20 },
    ],
    () => {
      dispatch({ type: 'UPGRADE_CONDITION', payload: { floorLevel }, timestamp: Date.now() });
      overlay.remove();
    },
  ));

  // Auto Purification
  const hygLevel = room.hygieneUpgrade ?? 0;
  container.appendChild(createUpgradeSection(
    t('room_upgrade_purify'),
    t('room_upgrade_purify_desc'),
    t('room_upgrade_purify_effect', { pct: 5 }),
    hygLevel,
    20,
    Math.round(400 * Math.pow(1.5, hygLevel)),
    state.sp,
    [
      { level: 10, text: t('room_upgrade_purify_bonus_10'), unlocked: hygLevel >= 10 },
      { level: 20, text: t('room_upgrade_purify_bonus_20'), unlocked: hygLevel >= 20 },
    ],
    () => {
      dispatch({ type: 'UPGRADE_HYGIENE', payload: { floorLevel }, timestamp: Date.now() });
      overlay.remove();
    },
  ));
}

// --- Room Detail: Demolish Tab ---
function renderDemolishTab(
  container: HTMLElement,
  floorLevel: number,
  overlay: HTMLElement,
): void {
  const warningEl = document.createElement('div');
  warningEl.className = 'room-detail-warning';
  warningEl.textContent = t('room_demolish_warning');
  container.appendChild(warningEl);

  const costEl = document.createElement('div');
  costEl.className = 'room-detail-status-ok';
  costEl.textContent = t('room_demolish_cost');
  container.appendChild(costEl);

  const demolishBtn = document.createElement('button');
  demolishBtn.className = 'popup-btn room-demolish-btn';
  demolishBtn.style.cssText = 'width:100%;margin-top:12px;min-height:48px;background:#dc2626;color:#fff;font-weight:700;border:none;border-radius:10px;cursor:pointer;';
  demolishBtn.textContent = t('room_demolish_btn');
  demolishBtn.addEventListener('click', () => {
    // Confirmation popup
    const confirmOverlay = document.createElement('div');
    confirmOverlay.className = 'popup-overlay';
    confirmOverlay.style.zIndex = '1100';
    confirmOverlay.addEventListener('click', (e) => { if (e.target === confirmOverlay) confirmOverlay.remove(); });

    const confirmBox = document.createElement('div');
    confirmBox.className = 'popup-box';
    const confirmMsg = document.createElement('div');
    confirmMsg.className = 'popup-message';
    confirmMsg.textContent = t('room_demolish_confirm');
    confirmBox.appendChild(confirmMsg);

    const btnRow = document.createElement('div');
    btnRow.className = 'popup-buttons';
    const cancelBtn = document.createElement('button');
    cancelBtn.className = 'popup-btn secondary';
    cancelBtn.textContent = t('cancel');
    cancelBtn.addEventListener('click', () => confirmOverlay.remove());
    btnRow.appendChild(cancelBtn);

    const confirmBtn = document.createElement('button');
    confirmBtn.className = 'popup-btn';
    confirmBtn.style.cssText = 'background:#dc2626;color:#fff;';
    confirmBtn.textContent = t('confirm');
    confirmBtn.addEventListener('click', () => {
      dispatch({ type: 'DEMOLISH_ROOM', payload: { floorLevel }, timestamp: Date.now() });
      confirmOverlay.remove();
      overlay.remove();
    });
    btnRow.appendChild(confirmBtn);

    confirmBox.appendChild(btnRow);
    confirmOverlay.appendChild(confirmBox);
    document.getElementById('popup-container')?.appendChild(confirmOverlay);
  });
  container.appendChild(demolishBtn);
}

// --- Helper: Gauge Bar ---

// --- Helper: Upgrade Section ---
function createUpgradeSection(
  title: string,
  desc: string,
  effect: string,
  currentLevel: number,
  maxLevel: number,
  cost: number,
  currentSp: number,
  bonuses: Array<{ level: number; text: string; unlocked: boolean }>,
  onUpgrade: () => void,
): HTMLElement {
  const section = document.createElement('div');
  section.className = 'room-upgrade-section';

  const titleEl = document.createElement('div');
  titleEl.className = 'room-upgrade-title';
  titleEl.textContent = title;
  section.appendChild(titleEl);

  const descEl = document.createElement('div');
  descEl.className = 'room-upgrade-desc';
  descEl.textContent = desc;
  section.appendChild(descEl);

  const effectEl = document.createElement('div');
  effectEl.className = 'room-upgrade-effect';
  effectEl.textContent = effect;
  section.appendChild(effectEl);

  // Level gauge bar
  const levelBar = document.createElement('div');
  levelBar.className = 'room-upgrade-level-bar';
  const levelFill = document.createElement('div');
  levelFill.className = 'room-upgrade-level-fill';
  levelFill.style.width = `${(currentLevel / maxLevel) * 100}%`;
  levelBar.appendChild(levelFill);
  section.appendChild(levelBar);

  const levelLabel = document.createElement('div');
  levelLabel.className = 'room-upgrade-level-label';
  levelLabel.textContent = t('room_upgrade_level', { cur: currentLevel, max: maxLevel });
  section.appendChild(levelLabel);

  // Bonus milestones
  for (const bonus of bonuses) {
    const bonusEl = document.createElement('div');
    bonusEl.className = `room-upgrade-bonus ${bonus.unlocked ? 'unlocked' : 'locked'}`;
    bonusEl.textContent = bonus.unlocked
      ? `${bonus.text} - ${t('room_upgrade_bonus_unlocked')}`
      : `${t('room_upgrade_bonus_locked', { n: bonus.level })}: ${bonus.text}`;
    section.appendChild(bonusEl);
  }

  // Upgrade button
  if (currentLevel < maxLevel) {
    const btn = document.createElement('button');
    btn.className = 'popup-btn primary';
    btn.style.cssText = 'width:100%;margin-top:8px;min-height:40px;font-size:12px;';
    btn.disabled = currentSp < cost;
    btn.textContent = t('room_upgrade_cost', { sp: formatSp(cost) });
    btn.addEventListener('click', onUpgrade);
    section.appendChild(btn);
  } else {
    const maxEl = document.createElement('div');
    maxEl.className = 'room-upgrade-max-label';
    maxEl.textContent = t('room_upgrade_max');
    section.appendChild(maxEl);
  }

  return section;
}

// === Ghost Click Handling ===
function handleGhostClick(ghostInstanceId: string): void {
  const state = gameState.get();
  const ghost = state.ghosts.find((g) => g.id === ghostInstanceId);
  if (!ghost) return;

  const def = GHOST_DEFS[ghost.characterId];
  const overlay = document.createElement('div');
  overlay.className = 'popup-overlay';
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });

  const box = document.createElement('div');
  box.className = 'popup-box';

  // Ghost image header
  const popupHeader = document.createElement('div');
  popupHeader.className = 'popup-ghost-header';
  const popupGhostImg = document.createElement('img');
  popupGhostImg.className = 'popup-ghost-img';
  popupGhostImg.src = def.image;
  popupGhostImg.alt = t(def.nameKey as Parameters<typeof t>[0]);
  popupGhostImg.draggable = false;
  popupHeader.appendChild(popupGhostImg);
  box.appendChild(popupHeader);

  const title = document.createElement('div');
  title.className = 'popup-title';
  title.style.textAlign = 'center';
  title.textContent = `${t(def.nameKey as Parameters<typeof t>[0])} - ${t('ghost_level', { n: ghost.level })}`;
  box.appendChild(title);

  // Skill info
  const skillEl = document.createElement('div');
  skillEl.className = 'ghost-detail-skill';
  skillEl.textContent = t(def.skillKey as Parameters<typeof t>[0]);
  box.appendChild(skillEl);

  // Training bonus
  if (ghost.trainingBonus > 0) {
    const bonusEl = document.createElement('div');
    bonusEl.style.cssText = 'font-size:12px;color:#34d399;margin-top:6px;padding:6px 8px;background:rgba(52,211,153,0.1);border-radius:6px;';
    bonusEl.textContent = `${t('training_bonus', { pct: Math.round(ghost.trainingBonus * 100) })} (${t('training_count', { n: ghost.trainingCount })})`;
    box.appendChild(bonusEl);
  }

  // Training in progress
  if (ghost.training) {
    const now = Date.now();
    const isComplete = now >= ghost.training.completesAt;

    if (isComplete) {
      const completeBtn = document.createElement('button');
      completeBtn.className = 'popup-btn primary';
      completeBtn.style.cssText = 'background:#34d399;color:#000;width:100%;min-height:44px;margin-top:8px;border-radius:10px;border:none;font-weight:600;cursor:pointer;';
      completeBtn.textContent = t('training_complete');
      completeBtn.addEventListener('click', () => {
        dispatch({ type: 'COMPLETE_TRAINING', payload: { ghostInstanceId }, timestamp: Date.now() });
        overlay.remove();
      });
      box.appendChild(completeBtn);
    } else {
      const remaining = Math.ceil((ghost.training.completesAt - now) / 1000);
      const progressEl = document.createElement('div');
      progressEl.style.cssText = 'font-size:12px;color:#fbbf24;margin-top:8px;';
      progressEl.textContent = `${t('training_in_progress')} ${remaining}s`;
      box.appendChild(progressEl);

      // Skip via ad
      const skipBtn = document.createElement('button');
      skipBtn.className = 'popup-btn';
      skipBtn.style.cssText = 'background:linear-gradient(135deg,#f59e0b,#d97706);color:#fff;width:100%;min-height:44px;margin-top:8px;border-radius:10px;border:none;font-weight:600;cursor:pointer;';
      skipBtn.textContent = t('training_skip_ad');
      skipBtn.addEventListener('click', () => {
        dispatch({ type: 'SKIP_TRAINING', payload: { ghostInstanceId }, timestamp: Date.now() });
        overlay.remove();
      });
      box.appendChild(skipBtn);
    }
  } else {
    // Fatigue recover
    if (ghost.fatigue > 0) {
      const recoverBtn = document.createElement('button');
      recoverBtn.className = 'popup-btn';
      recoverBtn.style.cssText = 'background:#ef4444;color:#fff;width:100%;min-height:44px;margin-top:8px;border-radius:10px;border:none;font-weight:600;cursor:pointer;';
      recoverBtn.disabled = state.sp < CONFIG.fatigueRecoveryCostSp;
      recoverBtn.textContent = t('recover_fatigue', { sp: CONFIG.fatigueRecoveryCostSp });
      recoverBtn.addEventListener('click', () => {
        dispatch({ type: 'RECOVER_FATIGUE', payload: { ghostInstanceId }, timestamp: Date.now() });
        overlay.remove();
      });
      box.appendChild(recoverBtn);

      // Free recovery via ad
      const adRecoverBtn = document.createElement('button');
      adRecoverBtn.className = 'popup-btn';
      adRecoverBtn.style.cssText = 'background:linear-gradient(135deg,#f59e0b,#d97706);color:#fff;width:100%;min-height:44px;margin-top:6px;border-radius:10px;border:none;font-weight:600;cursor:pointer;';
      adRecoverBtn.textContent = t('ad_recover_fatigue');
      adRecoverBtn.addEventListener('click', () => {
        dispatch({ type: 'WATCH_AD_RECOVER_FATIGUE', payload: { ghostInstanceId }, timestamp: Date.now() });
        overlay.remove();
      });
      box.appendChild(adRecoverBtn);
    }

    // Level up
    const expRequired = Math.floor(
      CONFIG.ghostLevelUpExpBase * Math.pow(CONFIG.ghostLevelUpExpGrowth, ghost.level - 1),
    );
    if (ghost.level < CONFIG.ghostMaxLevel && ghost.experience >= expRequired) {
      const lvlBtn = document.createElement('button');
      lvlBtn.className = 'popup-btn primary';
      lvlBtn.style.cssText = 'background:#34d399;color:#000;width:100%;min-height:44px;margin-top:8px;border-radius:10px;border:none;font-weight:600;cursor:pointer;';
      lvlBtn.textContent = t('ghost_level_up');
      lvlBtn.addEventListener('click', () => {
        dispatch({ type: 'LEVEL_UP_GHOST', payload: { ghostInstanceId }, timestamp: Date.now() });
        overlay.remove();
      });
      box.appendChild(lvlBtn);
    }

    // Training options (only if not assigned)
    if (!ghost.assignedRoomId) {
      const trainTitle = document.createElement('div');
      trainTitle.style.cssText = 'font-size:12px;color:#a855f7;font-weight:700;margin:10px 0 6px;';
      trainTitle.textContent = t('training_select');
      box.appendChild(trainTitle);

      for (const trainingId of ALL_TRAINING_IDS) {
        const trainingDef = TRAINING_DEFS[trainingId];
        const trainBtn = document.createElement('button');
        trainBtn.className = 'popup-btn secondary';
        trainBtn.style.cssText = 'width:100%;margin-bottom:4px;min-height:40px;text-align:left;font-size:11px;';
        trainBtn.disabled = state.sp < trainingDef.costSp;
        trainBtn.textContent = `${t(trainingDef.nameKey as Parameters<typeof t>[0])} - ${formatSp(trainingDef.costSp)} SP (${Math.round(trainingDef.durationMs / 1000)}s)`;
        trainBtn.addEventListener('click', () => {
          dispatch({ type: 'TRAIN_GHOST', payload: { ghostInstanceId, trainingId: trainingId as TrainingId }, timestamp: Date.now() });
          overlay.remove();
        });
        box.appendChild(trainBtn);
      }
    } else {
      // Unassign
      const unassignBtn = document.createElement('button');
      unassignBtn.className = 'popup-btn secondary';
      unassignBtn.style.cssText = 'width:100%;min-height:44px;margin-top:8px;border-radius:10px;border:none;font-weight:600;cursor:pointer;background:#2a2a4a;color:#f0e8ff;';
      unassignBtn.textContent = t('unassign_ghost');
      unassignBtn.addEventListener('click', () => {
        dispatch({ type: 'UNASSIGN_GHOST', payload: { ghostInstanceId }, timestamp: Date.now() });
        overlay.remove();
      });
      box.appendChild(unassignBtn);
    }
  }

  const closeBtn = document.createElement('button');
  closeBtn.className = 'popup-btn secondary';
  closeBtn.style.cssText = 'width:100%;min-height:44px;margin-top:8px;border-radius:10px;border:none;font-weight:600;cursor:pointer;background:#2a2a4a;color:#f0e8ff;';
  closeBtn.textContent = t('close');
  closeBtn.addEventListener('click', () => overlay.remove());
  box.appendChild(closeBtn);

  overlay.appendChild(box);
  popupContainer.appendChild(overlay);
}

// === Day Summary Popup ===
function showDaySummary(state: GameStateData): void {
  const overlay = document.createElement('div');
  overlay.className = 'popup-overlay';

  const box = document.createElement('div');
  box.className = 'day-summary-popup';

  // ── Header ──
  const header = document.createElement('div');
  header.className = 'day-summary-header';
  header.textContent = t('day_summary_title', { n: state.dayNumber });
  box.appendChild(header);

  // ── Best Visitor (highest rated review) ──
  const todayReviews = state.reviews.filter((_) => {
    // reviews from today (within last day's earnings period)
    return true; // all current reviews are from today since reviews accumulate
  });

  if (todayReviews.length > 0) {
    const bestReview = todayReviews.reduce((best, r) => r.stars > best.stars ? r : best, todayReviews[0]);

    const visitorSection = document.createElement('div');
    visitorSection.className = 'day-summary-visitor';

    // Stars display
    const starsRow = document.createElement('div');
    starsRow.className = 'day-summary-stars';
    const starStr = '\u2605'.repeat(bestReview.stars) + '\u2606'.repeat(5 - bestReview.stars);
    starsRow.textContent = starStr;
    visitorSection.appendChild(starsRow);

    // Star visit label
    const visitLabel = document.createElement('div');
    visitLabel.className = 'day-summary-visit-label';
    visitLabel.textContent = t('day_summary_best_visitor', { n: bestReview.stars });
    visitorSection.appendChild(visitLabel);

    // Reaction text
    const reactionKey = `day_summary_reaction_${bestReview.stars}` as Parameters<typeof t>[0];
    const reactionEl = document.createElement('div');
    reactionEl.className = 'day-summary-reaction';
    reactionEl.textContent = t(reactionKey);
    visitorSection.appendChild(reactionEl);

    box.appendChild(visitorSection);
  }

  // ── Stats: 2-column layout ──
  const statsGrid = document.createElement('div');
  statsGrid.className = 'day-summary-stats';

  // Left: Total SP Collected
  const spBox = document.createElement('div');
  spBox.className = 'day-summary-stat-box';

  const spLabel = document.createElement('div');
  spLabel.className = 'day-summary-stat-label';
  spLabel.textContent = t('day_summary_sp_collected');
  spBox.appendChild(spLabel);

  const spValue = document.createElement('div');
  spValue.className = 'day-summary-stat-value';
  spValue.textContent = formatSp(state.totalSpEarned);
  spBox.appendChild(spValue);

  const spGain = document.createElement('div');
  spGain.className = 'day-summary-stat-gain';
  spGain.textContent = t('day_summary_sp_gain', { n: formatSp(state.todayEarnings) });
  spBox.appendChild(spGain);

  statsGrid.appendChild(spBox);

  // Right: Daily Milestones
  const milestonesBox = document.createElement('div');
  milestonesBox.className = 'day-summary-stat-box';

  const milLabel = document.createElement('div');
  milLabel.className = 'day-summary-stat-label';
  milLabel.textContent = t('day_summary_milestones');
  milestonesBox.appendChild(milLabel);

  if (state.dailyMilestones.length > 0) {
    const milList = document.createElement('div');
    milList.className = 'day-summary-milestone-list';

    for (const m of state.dailyMilestones) {
      const milItem = document.createElement('div');
      milItem.className = `day-summary-milestone-item${m.completed ? ' completed' : ''}`;

      const checkbox = document.createElement('span');
      checkbox.className = 'day-summary-milestone-check';
      checkbox.textContent = m.completed ? '\u2611' : '\u2610';
      milItem.appendChild(checkbox);

      const milText = document.createElement('span');
      milText.className = 'day-summary-milestone-text';
      milText.textContent = t(m.nameKey as Parameters<typeof t>[0], { target: m.target });
      milItem.appendChild(milText);

      if (m.completed && !m.collected) {
        const rewardBadge = document.createElement('span');
        rewardBadge.className = 'day-summary-milestone-reward';
        rewardBadge.textContent = t('milestone_reward', { n: m.reward });
        milItem.appendChild(rewardBadge);
      }

      milList.appendChild(milItem);
    }

    milestonesBox.appendChild(milList);
  } else {
    const emptyMil = document.createElement('div');
    emptyMil.className = 'day-summary-milestone-empty';
    emptyMil.textContent = '--';
    milestonesBox.appendChild(emptyMil);
  }

  statsGrid.appendChild(milestonesBox);
  box.appendChild(statsGrid);

  // ── Total Visits Progress Bar ──
  const maxVisitors = 50; // daily capacity goal
  const streetCount = state.streetVisitors?.length ?? 0;
  const streetSpent = state.streetVisitors?.reduce((sum, v) => sum + v.totalSpent, 0) ?? 0;
  const totalVisitors = state.todayVisitors + streetCount;

  const visitsSection = document.createElement('div');
  visitsSection.className = 'day-summary-visits';

  const visitsLabel = document.createElement('div');
  visitsLabel.className = 'day-summary-visits-label';
  visitsLabel.textContent = `${t('day_summary_total_visits')}: ${totalVisitors}/${maxVisitors}`;
  visitsSection.appendChild(visitsLabel);

  const progressTrack = document.createElement('div');
  progressTrack.className = 'day-summary-progress-track';

  const progressFill = document.createElement('div');
  progressFill.className = 'day-summary-progress-fill';
  const pct = Math.min(100, (totalVisitors / maxVisitors) * 100);
  progressFill.style.width = `${pct}%`;
  progressTrack.appendChild(progressFill);

  visitsSection.appendChild(progressTrack);

  // Street visitor breakdown
  if (streetCount > 0) {
    const streetLine = document.createElement('div');
    streetLine.className = 'day-summary-visits-label';
    streetLine.style.marginTop = '4px';
    streetLine.style.fontSize = '11px';
    streetLine.style.opacity = '0.8';
    streetLine.textContent = `${t('day_summary_street_visitors')}: ${streetCount} / ${t('day_summary_street_revenue')}: ${formatSp(streetSpent)}`;
    visitsSection.appendChild(streetLine);
  }

  box.appendChild(visitsSection);

  // ── End Day Button ──
  const nextBtn = document.createElement('button');
  nextBtn.className = 'day-summary-btn-primary';
  nextBtn.textContent = t('day_summary_next');
  nextBtn.addEventListener('click', () => {
    // Collect milestone rewards first
    dispatch({ type: 'COLLECT_MILESTONE_REWARDS', payload: {}, timestamp: Date.now() });
    dispatch({ type: 'END_DAY', payload: {}, timestamp: Date.now() });
    overlay.remove();
  });
  box.appendChild(nextBtn);

  // ── Close Button ──
  const closeBtn = document.createElement('button');
  closeBtn.className = 'day-summary-btn-close';
  closeBtn.textContent = t('close');
  closeBtn.addEventListener('click', () => overlay.remove());
  box.appendChild(closeBtn);

  overlay.appendChild(box);
  popupContainer.appendChild(overlay);
}

// === New Day Summary (with salary deductions) ===
function showDaySummaryNew(state: GameStateData): void {
  playChime();
  const overlay = document.createElement('div');
  overlay.className = 'popup-overlay';

  const box = document.createElement('div');
  box.className = 'day-summary-popup';

  // Header
  const header = document.createElement('div');
  header.className = 'day-summary-header';
  header.textContent = t('day_summary_title', { n: state.dayNumber });
  box.appendChild(header);

  // --- Highlight: Best Review Card ---
  const todayReviews = state.reviews.filter(() => true);
  if (todayReviews.length > 0) {
    const bestReview = todayReviews.reduce((best, r) => r.stars > best.stars ? r : best, todayReviews[0]);
    const highlightCard = document.createElement('div');
    highlightCard.className = 'ds-highlight-card';

    const hlBadge = document.createElement('div');
    hlBadge.className = 'ds-highlight-badge';
    hlBadge.textContent = t('day_summary_highlight');
    highlightCard.appendChild(hlBadge);

    const starsRow = document.createElement('div');
    starsRow.className = 'day-summary-stars';
    starsRow.textContent = '\u2605'.repeat(bestReview.stars) + '\u2606'.repeat(5 - bestReview.stars);
    highlightCard.appendChild(starsRow);

    const reactionKey = `day_summary_reaction_${bestReview.stars}` as Parameters<typeof t>[0];
    const reactionEl = document.createElement('div');
    reactionEl.className = 'ds-highlight-reaction';
    reactionEl.textContent = t(reactionKey);
    highlightCard.appendChild(reactionEl);

    // Show review text
    let reviewText = t(bestReview.textKey as Parameters<typeof t>[0]);
    if (bestReview.ghostNameKey) {
      const ghostName = t(bestReview.ghostNameKey as Parameters<typeof t>[0]);
      reviewText = reviewText.replace('{ghost}', ghostName);
    }
    const reviewTextEl = document.createElement('div');
    reviewTextEl.className = 'ds-highlight-text';
    reviewTextEl.textContent = `"${reviewText}"`;
    highlightCard.appendChild(reviewTextEl);

    if (bestReview.visitorType) {
      const typeEl = document.createElement('div');
      typeEl.className = 'ds-highlight-visitor-type';
      typeEl.textContent = `- ${bestReview.visitorType.replace('_', ' ')} -`;
      highlightCard.appendChild(typeEl);
    }

    box.appendChild(highlightCard);
  }

  // --- Stats Row: Earnings / Avg Rating / Fame ---
  const statsGrid = document.createElement('div');
  statsGrid.className = 'day-summary-stats';

  // Earnings
  const spBox = document.createElement('div');
  spBox.className = 'day-summary-stat-box';
  const spLabel = document.createElement('div');
  spLabel.className = 'day-summary-stat-label';
  spLabel.textContent = t('day_summary_sp_collected');
  spBox.appendChild(spLabel);
  const spValue = document.createElement('div');
  spValue.className = 'day-summary-stat-value';
  spValue.textContent = formatSp(state.todayEarnings);
  spBox.appendChild(spValue);
  statsGrid.appendChild(spBox);

  // Average Rating
  if (todayReviews.length > 0) {
    const avgStars = todayReviews.reduce((sum, r) => sum + r.stars, 0) / todayReviews.length;
    const avgBox = document.createElement('div');
    avgBox.className = 'day-summary-stat-box';
    const avgLabel = document.createElement('div');
    avgLabel.className = 'day-summary-stat-label';
    avgLabel.textContent = t('day_summary_avg_stars');
    avgBox.appendChild(avgLabel);
    const avgValue = document.createElement('div');
    avgValue.className = 'day-summary-stat-value';
    avgValue.textContent = avgStars.toFixed(1);
    avgBox.appendChild(avgValue);
    const avgStarsVis = document.createElement('div');
    avgStarsVis.className = 'ds-mini-stars';
    avgStarsVis.textContent = '\u2605'.repeat(Math.round(avgStars)) + '\u2606'.repeat(5 - Math.round(avgStars));
    avgBox.appendChild(avgStarsVis);
    statsGrid.appendChild(avgBox);
  }

  // Salaries
  const salaries = state.todaySalaries || 0;
  if (salaries > 0) {
    const salBox = document.createElement('div');
    salBox.className = 'day-summary-stat-box';
    const salLabel = document.createElement('div');
    salLabel.className = 'day-summary-stat-label';
    salLabel.textContent = t('day_summary_salaries');
    salBox.appendChild(salLabel);
    const salValue = document.createElement('div');
    salValue.className = 'day-summary-stat-value';
    salValue.style.color = '#ef4444';
    salValue.textContent = `-${formatSp(salaries)}`;
    salBox.appendChild(salValue);
    statsGrid.appendChild(salBox);
  }

  // Fame change
  const fameMap: Record<number, number> = { 1: -2, 2: 0, 3: 1, 4: 3, 5: 5 };
  const fameChange = todayReviews.reduce((sum, r) => sum + (fameMap[r.stars] ?? 0), 0);
  if (todayReviews.length > 0) {
    const fameBox = document.createElement('div');
    fameBox.className = 'day-summary-stat-box';
    const fameLabel = document.createElement('div');
    fameLabel.className = 'day-summary-stat-label';
    fameLabel.textContent = t('day_summary_fame_change');
    fameBox.appendChild(fameLabel);
    const fameValue = document.createElement('div');
    fameValue.className = 'day-summary-stat-value';
    fameValue.style.color = fameChange >= 0 ? '#34d399' : '#ef4444';
    fameValue.textContent = `${fameChange >= 0 ? '+' : ''}${fameChange}`;
    fameBox.appendChild(fameValue);
    statsGrid.appendChild(fameBox);
  }

  box.appendChild(statsGrid);

  // Net earnings
  const netEarnings = state.todayEarnings - salaries;
  const netSection = document.createElement('div');
  netSection.style.cssText = 'text-align:center;margin:12px 0;';
  const netLabel = document.createElement('div');
  netLabel.style.cssText = 'font-size:11px;color:#a08ccc;';
  netLabel.textContent = t('day_summary_net_earnings');
  netSection.appendChild(netLabel);
  const netValue = document.createElement('div');
  netValue.style.cssText = `font-size:24px;font-weight:700;color:${netEarnings >= 0 ? '#34d399' : '#ef4444'};`;
  netValue.textContent = `${netEarnings >= 0 ? '+' : ''}${formatSp(netEarnings)} SP`;
  netSection.appendChild(netValue);
  box.appendChild(netSection);

  // --- Review Feed (SNS-style cards) ---
  if (todayReviews.length > 0) {
    const feedSection = document.createElement('div');
    feedSection.className = 'ds-review-feed';

    const feedTitle = document.createElement('div');
    feedTitle.className = 'ds-review-feed-title';
    feedTitle.textContent = t('day_summary_reviews_title');
    feedSection.appendChild(feedTitle);

    // Show up to 5 most recent reviews (newest first)
    const displayReviews = [...todayReviews].reverse().slice(0, 5);
    for (let i = 0; i < displayReviews.length; i++) {
      const r = displayReviews[i];
      const card = document.createElement('div');
      card.className = `ds-review-card stars-${r.stars}`;

      const cardHeader = document.createElement('div');
      cardHeader.className = 'ds-review-card-header';

      const cardStars = document.createElement('span');
      cardStars.className = 'ds-review-card-stars';
      cardStars.textContent = '\u2605'.repeat(r.stars);
      cardHeader.appendChild(cardStars);

      const cardType = document.createElement('span');
      cardType.className = 'ds-review-card-type';
      cardType.textContent = r.visitorType ? r.visitorType.replace('_', ' ') : t('day_summary_review_visitor', { n: i + 1 });
      cardHeader.appendChild(cardType);

      card.appendChild(cardHeader);

      let rText = t(r.textKey as Parameters<typeof t>[0]);
      if (r.ghostNameKey) {
        const gName = t(r.ghostNameKey as Parameters<typeof t>[0]);
        rText = rText.replace('{ghost}', gName);
      }
      const cardBody = document.createElement('div');
      cardBody.className = 'ds-review-card-body';
      cardBody.textContent = rText;
      card.appendChild(cardBody);

      feedSection.appendChild(card);
    }

    box.appendChild(feedSection);
  }

  // Visits progress (include street visitors)
  const maxVisitors = 50;
  const streetCountNew = state.streetVisitors?.length ?? 0;
  const streetSpentNew = state.streetVisitors?.reduce((sum, v) => sum + v.totalSpent, 0) ?? 0;
  const totalVisitorsNew = state.todayVisitors + streetCountNew;
  const visitsSection = document.createElement('div');
  visitsSection.className = 'day-summary-visits';
  const visitsLabel = document.createElement('div');
  visitsLabel.className = 'day-summary-visits-label';
  visitsLabel.textContent = `${t('day_summary_total_visits')}: ${totalVisitorsNew}/${maxVisitors}`;
  visitsSection.appendChild(visitsLabel);
  const progressTrack = document.createElement('div');
  progressTrack.className = 'day-summary-progress-track';
  const progressFill = document.createElement('div');
  progressFill.className = 'day-summary-progress-fill';
  const pct = Math.min(100, (totalVisitorsNew / maxVisitors) * 100);
  progressFill.style.width = `${pct}%`;
  progressTrack.appendChild(progressFill);
  visitsSection.appendChild(progressTrack);
  if (streetCountNew > 0) {
    const streetLine = document.createElement('div');
    streetLine.className = 'day-summary-visits-label';
    streetLine.style.cssText = 'margin-top:4px;font-size:11px;opacity:0.8;';
    streetLine.textContent = `${t('day_summary_street_visitors')}: ${streetCountNew} / ${t('day_summary_street_revenue')}: ${formatSp(streetSpentNew)}`;
    visitsSection.appendChild(streetLine);
  }
  box.appendChild(visitsSection);

  // Milestones
  if (state.dailyMilestones.length > 0) {
    const milList = document.createElement('div');
    milList.className = 'day-summary-milestone-list';
    for (const m of state.dailyMilestones) {
      const milItem = document.createElement('div');
      milItem.className = `day-summary-milestone-item${m.completed ? ' completed' : ''}`;
      const checkbox = document.createElement('span');
      checkbox.className = 'day-summary-milestone-check';
      checkbox.textContent = m.completed ? '\u2611' : '\u2610';
      milItem.appendChild(checkbox);
      const milText = document.createElement('span');
      milText.className = 'day-summary-milestone-text';
      milText.textContent = t(m.nameKey as Parameters<typeof t>[0], { target: m.target });
      milItem.appendChild(milText);
      milList.appendChild(milItem);
    }
    box.appendChild(milList);
  }

  // Next Day button
  const nextBtn = document.createElement('button');
  nextBtn.className = 'day-summary-btn-primary';
  nextBtn.textContent = t('day_summary_next');
  nextBtn.addEventListener('click', () => {
    dispatch({ type: 'COLLECT_MILESTONE_REWARDS', payload: {}, timestamp: Date.now() });
    dispatch({ type: 'ADVANCE_TO_DAY', payload: {}, timestamp: Date.now() });
    overlay.remove();
  });
  box.appendChild(nextBtn);

  overlay.appendChild(box);
  popupContainer.appendChild(overlay);
}

// === Menu Panel with Accordion Sections ===
let menuExpandedSections = new Set<string>(['facilities']);

function renderMenuPanel(container: HTMLElement, state: GameStateData): void {
  container.innerHTML = '';

  const sections = [
    { id: 'facilities', titleKey: 'facilities_tab' as Parameters<typeof t>[0], iconClass: 'facilities' },
    { id: 'reviews', titleKey: 'reviews_tab' as Parameters<typeof t>[0], iconClass: 'reviews' },
    { id: 'prestige', titleKey: 'prestige_tab' as Parameters<typeof t>[0], iconClass: 'prestige' },
  ];

  for (const section of sections) {
    const sectionEl = document.createElement('div');
    sectionEl.className = 'menu-section';
    if (menuExpandedSections.has(section.id)) {
      sectionEl.classList.add('expanded');
    }

    // Header
    const header = document.createElement('button');
    header.className = 'menu-section-header';

    const icon = document.createElement('span');
    icon.className = `section-icon ${section.iconClass}`;
    // Use first letter as icon
    icon.textContent = t(section.titleKey).charAt(0).toUpperCase();
    header.appendChild(icon);

    const titleSpan = document.createElement('span');
    titleSpan.textContent = t(section.titleKey);
    header.appendChild(titleSpan);

    const arrow = document.createElement('span');
    arrow.className = 'section-arrow';
    arrow.textContent = '>';
    header.appendChild(arrow);

    header.addEventListener('click', () => {
      if (menuExpandedSections.has(section.id)) {
        menuExpandedSections.delete(section.id);
      } else {
        menuExpandedSections.add(section.id);
      }
      renderMenuPanel(container, gameState.get());
    });
    sectionEl.appendChild(header);

    // Body
    const body = document.createElement('div');
    body.className = 'menu-section-body';

    if (menuExpandedSections.has(section.id)) {
      if (section.id === 'facilities') {
        renderFacilities(
          body,
          state,
          (action, facilityId) => {
            if (action === 'build') {
              dispatch({ type: 'BUILD_FACILITY', payload: { facilityId: facilityId as FacilityId }, timestamp: Date.now() });
            } else {
              dispatch({ type: 'UPGRADE_FACILITY', payload: { facilityId: facilityId as FacilityId }, timestamp: Date.now() });
            }
          },
          (action) => {
            if (action === 'sp_boost') {
              dispatch({ type: 'WATCH_AD_SP_BOOST', payload: {}, timestamp: Date.now() });
            } else if (action === 'vip_visitor') {
              dispatch({ type: 'WATCH_AD_VIP_VISITOR', payload: {}, timestamp: Date.now() });
            }
          },
        );
      } else if (section.id === 'reviews') {
        renderReviews(body, state);
      } else if (section.id === 'prestige') {
        renderPrestige(body, state, () => {
          showPrestigeConfirm(state);
        });
      }
    }

    sectionEl.appendChild(body);
    container.appendChild(sectionEl);
  }
}

// === Facilities Management Panel ===
function renderFacilitiesMgmtPanel(container: HTMLElement, state: GameStateData): void {
  container.innerHTML = '';

  // -- Open/Close Business Button (top) --
  const openBtn = document.createElement('button');
  openBtn.className = state.isOpen ? 'menu-wide-btn danger' : 'menu-wide-btn success';
  openBtn.style.cssText = 'margin-bottom:12px;font-size:16px;min-height:52px;font-weight:800;letter-spacing:1px;';
  openBtn.textContent = state.isOpen ? t('toggle_close') : t('toggle_open');
  openBtn.addEventListener('click', () => {
    dispatch({ type: 'TOGGLE_OPEN', payload: {}, timestamp: Date.now() });
  });
  container.appendChild(openBtn);

  // -- Facility Management Section --
  const sectionTitle = document.createElement('div');
  sectionTitle.className = 'panel-section-title';
  sectionTitle.textContent = t('facilities_mgmt_title');
  container.appendChild(sectionTitle);

  const roomsWithData = state.building.filter((f) => f.room !== null);
  if (roomsWithData.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'panel-empty-text';
    empty.textContent = t('facilities_view_empty');
    container.appendChild(empty);
  } else {
    for (const floor of state.building) {
      if (!floor.room) continue;
      const room = floor.room;
      const roomDef = ROOM_TYPES[room.type];
      const condition = room.condition ?? 100;
      const hygiene = room.hygiene ?? 100;

      const card = document.createElement('div');
      card.className = 'facility-mgmt-card';

      const cardHeader = document.createElement('div');
      cardHeader.className = 'facility-mgmt-header';
      cardHeader.textContent = `${t('floor_label', { n: floor.level })} - ${t(roomDef.nameKey as Parameters<typeof t>[0])}`;
      card.appendChild(cardHeader);

      // Condition gauge
      const condRow = document.createElement('div');
      condRow.className = 'gauge-row';
      const condLabel = document.createElement('span');
      condLabel.className = 'gauge-label';
      condLabel.textContent = t('facility_condition');
      condRow.appendChild(condLabel);
      const condBar = document.createElement('div');
      condBar.className = 'gauge-bar';
      const condFill = document.createElement('div');
      condFill.className = `gauge-fill ${condition < 30 ? 'danger' : condition < 60 ? 'warning' : 'good'}`;
      condFill.style.width = `${condition}%`;
      condBar.appendChild(condFill);
      condRow.appendChild(condBar);
      const condVal = document.createElement('span');
      condVal.className = `gauge-value ${condition < 30 ? 'danger-text' : ''}`;
      condVal.textContent = `${condition}`;
      condRow.appendChild(condVal);
      card.appendChild(condRow);
      if (condition < 30) {
        const warn = document.createElement('div');
        warn.className = 'gauge-warning';
        warn.textContent = t('facility_condition_warning');
        card.appendChild(warn);
      }

      // Hygiene gauge
      const hygRow = document.createElement('div');
      hygRow.className = 'gauge-row';
      const hygLabel = document.createElement('span');
      hygLabel.className = 'gauge-label';
      hygLabel.textContent = t('facility_hygiene');
      hygRow.appendChild(hygLabel);
      const hygBar = document.createElement('div');
      hygBar.className = 'gauge-bar';
      const hygFill = document.createElement('div');
      hygFill.className = `gauge-fill ${hygiene < 30 ? 'danger' : hygiene < 60 ? 'warning' : 'good'}`;
      hygFill.style.width = `${hygiene}%`;
      hygBar.appendChild(hygFill);
      hygRow.appendChild(hygBar);
      const hygVal = document.createElement('span');
      hygVal.className = `gauge-value ${hygiene < 30 ? 'danger-text' : ''}`;
      hygVal.textContent = `${hygiene}`;
      hygRow.appendChild(hygVal);
      card.appendChild(hygRow);
      if (hygiene < 30) {
        const warn = document.createElement('div');
        warn.className = 'gauge-warning';
        warn.textContent = t('facility_hygiene_warning');
        card.appendChild(warn);
      }

      // Dispatch buttons
      const btnRow = document.createElement('div');
      btnRow.className = 'facility-mgmt-btns';
      const repairBtn = document.createElement('button');
      repairBtn.className = 'dispatch-btn repair-dispatch';
      repairBtn.textContent = t('dispatch_repair');
      const hasRepairStaff = state.ghosts.some((g) => g.role === 'repair' && !g.assignedRoomId);
      repairBtn.disabled = !hasRepairStaff || condition >= 100;
      repairBtn.addEventListener('click', () => {
        // Dispatch repair: find available repair ghost, improve condition
        const repairGhost = state.ghosts.find((g) => g.role === 'repair' && !g.assignedRoomId);
        if (repairGhost && room) {
          // We update via inline state set since this is a UI-only action for now
          const newBuilding = state.building.map((fl) => {
            if (fl.room && fl.room.id === room.id) {
              return { ...fl, room: { ...fl.room, condition: Math.min(100, (fl.room.condition ?? 100) + 30) } };
            }
            return fl;
          });
          gameState.set({ ...state, building: newBuilding });
        }
      });
      btnRow.appendChild(repairBtn);

      const cleanBtn = document.createElement('button');
      cleanBtn.className = 'dispatch-btn clean-dispatch';
      cleanBtn.textContent = t('dispatch_clean');
      const hasJanitor = state.ghosts.some((g) => g.role === 'janitor' && !g.assignedRoomId);
      cleanBtn.disabled = !hasJanitor || hygiene >= 100;
      cleanBtn.addEventListener('click', () => {
        const janitorGhost = state.ghosts.find((g) => g.role === 'janitor' && !g.assignedRoomId);
        if (janitorGhost && room) {
          const newBuilding = state.building.map((fl) => {
            if (fl.room && fl.room.id === room.id) {
              return { ...fl, room: { ...fl.room, hygiene: Math.min(100, (fl.room.hygiene ?? 100) + 30) } };
            }
            return fl;
          });
          gameState.set({ ...state, building: newBuilding });
        }
      });
      btnRow.appendChild(cleanBtn);
      card.appendChild(btnRow);

      container.appendChild(card);
    }
  }

  // -- Ticket Price Section --
  const ticketSection = document.createElement('div');
  ticketSection.className = 'panel-section';

  const ticketTitle = document.createElement('div');
  ticketTitle.className = 'panel-section-title';
  ticketTitle.textContent = t('ticket_section_title');
  ticketSection.appendChild(ticketTitle);

  const priceRow = document.createElement('div');
  priceRow.className = 'ticket-price-row';

  const minusBtn = document.createElement('button');
  minusBtn.className = 'ticket-btn';
  minusBtn.textContent = '-';
  minusBtn.disabled = state.ticketPrice <= 5;
  minusBtn.addEventListener('click', () => {
    dispatch({ type: 'SET_TICKET_PRICE', payload: { price: Math.max(5, state.ticketPrice - 5) }, timestamp: Date.now() });
  });
  priceRow.appendChild(minusBtn);

  const priceDisplay = document.createElement('div');
  priceDisplay.className = 'ticket-price-display';
  priceDisplay.textContent = `${state.ticketPrice} SP`;
  priceRow.appendChild(priceDisplay);

  const plusBtn = document.createElement('button');
  plusBtn.className = 'ticket-btn';
  plusBtn.textContent = '+';
  plusBtn.disabled = state.ticketPrice >= 200;
  plusBtn.addEventListener('click', () => {
    dispatch({ type: 'SET_TICKET_PRICE', payload: { price: Math.min(200, state.ticketPrice + 5) }, timestamp: Date.now() });
  });
  priceRow.appendChild(plusBtn);
  ticketSection.appendChild(priceRow);

  // Expected visitors hint
  const expectedVisitors = Math.max(1, Math.round(30 - (state.ticketPrice - 10) * 0.2));
  const hint = document.createElement('div');
  hint.className = 'ticket-hint';
  hint.textContent = t('expected_visitors', { n: expectedVisitors });
  ticketSection.appendChild(hint);

  container.appendChild(ticketSection);

  // -- Star Evolution Section --
  const starSection = document.createElement('div');
  starSection.className = 'panel-section';

  const starTitle = document.createElement('div');
  starTitle.className = 'panel-section-title';
  starTitle.textContent = t('star_rating_title');
  starSection.appendChild(starTitle);

  const starEvolution = state.starEvolution ?? 0;
  const starLevel = state.starLevel ?? 0;
  const STAR_NAMES: Parameters<typeof t>[0][] = ['star_level_gray', 'star_level_bronze', 'star_level_silver', 'star_level_gold', 'star_level_rainbow'];
  const STAR_COLORS = ['#9ca3af', '#cd7f32', '#c0c0c0', '#ffd700', 'linear-gradient(90deg,#ef4444,#f59e0b,#eab308,#22c55e,#3b82f6,#8b5cf6)'];

  const starDisplay = document.createElement('div');
  starDisplay.className = 'star-display';

  // 5 stars
  for (let i = 0; i < 5; i++) {
    const star = document.createElement('div');
    star.className = `star-icon ${i < (starLevel % 5 === 0 && starLevel > 0 ? 5 : starLevel % 5) ? 'filled' : 'empty'}`;
    if (i < (starLevel % 5 === 0 && starLevel > 0 ? 5 : starLevel % 5)) {
      if (STAR_COLORS[starEvolution].includes('gradient')) {
        star.style.background = STAR_COLORS[starEvolution];
        star.style.backgroundClip = 'text';
        star.style.webkitBackgroundClip = 'text';
        star.style.webkitTextFillColor = 'transparent';
      } else {
        star.style.color = STAR_COLORS[starEvolution];
      }
    }
    star.textContent = '*';
    starDisplay.appendChild(star);
  }
  starSection.appendChild(starDisplay);

  const starLabel = document.createElement('div');
  starLabel.className = 'star-evolution-label';
  starLabel.textContent = t(STAR_NAMES[starEvolution]);
  if (starEvolution >= 4 && starLevel >= 25) {
    starLabel.textContent = t('star_evolution_max');
    starLabel.classList.add('star-max');
  }
  starSection.appendChild(starLabel);

  const starProgress = document.createElement('div');
  starProgress.className = 'star-progress-text';
  starProgress.textContent = t('star_current', { cur: starLevel, max: 25 });
  starSection.appendChild(starProgress);

  container.appendChild(starSection);
}

// === Hire Panel ===
function renderHirePanel(container: HTMLElement, state: GameStateData): void {
  container.innerHTML = '';

  // Scare Specialists section
  const scareTitle = document.createElement('div');
  scareTitle.className = 'panel-section-title';
  scareTitle.textContent = t('hire_scare_section');
  container.appendChild(scareTitle);

  // Render ghost shop using existing function (hide room types from recruitment tab)
  renderShop(
    container,
    state,
    (_action, payload) => {
      dispatch({ type: 'HIRE_GHOST', payload: { ghostCharacterId: payload.ghostCharacterId as import('./game/data/ghosts.js').GhostId }, timestamp: Date.now() });
    },
    (_action, payload) => {
      dispatch({ type: 'UNLOCK_ROOM_TYPE', payload: { roomTypeId: payload.roomTypeId as import('./game/data/rooms.js').RoomTypeId }, timestamp: Date.now() });
    },
    { hideRoomTypes: true },
  );

  // Facility Staff section
  const staffTitle = document.createElement('div');
  staffTitle.className = 'panel-section-title';
  staffTitle.style.marginTop = '16px';
  staffTitle.textContent = t('hire_staff_section');
  container.appendChild(staffTitle);

  const staffRoles: { role: import('./game/state.js').StaffRole; nameKey: Parameters<typeof t>[0]; descKey: Parameters<typeof t>[0]; cost: number }[] = [
    { role: 'repair', nameKey: 'staff_role_repair', descKey: 'staff_role_repair_desc', cost: 800 },
    { role: 'janitor', nameKey: 'staff_role_janitor', descKey: 'staff_role_janitor_desc', cost: 600 },
    { role: 'ticket', nameKey: 'staff_role_ticket', descKey: 'staff_role_ticket_desc', cost: 500 },
    { role: 'guide', nameKey: 'staff_role_guide', descKey: 'staff_role_guide_desc', cost: 1000 },
  ];

  // Use a generic ghost for each role (cycle through available ghost defs)
  const genericGhostIds = ['kappa', 'pierrot', 'jinaeryeong', 'gangsi'] as import('./game/data/ghosts.js').GhostId[];

  for (let i = 0; i < staffRoles.length; i++) {
    const sr = staffRoles[i];
    const ghostId = genericGhostIds[i % genericGhostIds.length];
    const ghostDef = GHOST_DEFS[ghostId];

    const item = document.createElement('div');
    item.className = 'shop-item';

    const avatarEl = document.createElement('div');
    avatarEl.className = 'shop-ghost-avatar';
    const avatarImg = document.createElement('img');
    avatarImg.className = 'shop-ghost-img';
    avatarImg.src = ghostDef.image;
    avatarImg.alt = t(sr.nameKey);
    avatarImg.draggable = false;
    avatarEl.appendChild(avatarImg);
    item.appendChild(avatarEl);

    const infoEl = document.createElement('div');
    infoEl.className = 'shop-item-info';
    const nameEl = document.createElement('div');
    nameEl.className = 'shop-item-name';
    nameEl.textContent = t(sr.nameKey);
    infoEl.appendChild(nameEl);
    const descEl = document.createElement('div');
    descEl.className = 'shop-item-sub';
    descEl.textContent = t(sr.descKey);
    infoEl.appendChild(descEl);
    const costEl = document.createElement('div');
    costEl.className = 'shop-item-cost';
    costEl.textContent = t('hire_cost_label', { sp: formatSp(sr.cost) });
    infoEl.appendChild(costEl);
    item.appendChild(infoEl);

    const btn = document.createElement('button');
    btn.className = 'shop-btn';
    btn.disabled = state.sp < sr.cost;
    btn.textContent = t('hire');
    btn.addEventListener('click', () => {
      // Hire a generic ghost with the staff role
      dispatch({ type: 'HIRE_GHOST', payload: { ghostCharacterId: ghostId }, timestamp: Date.now() });
      // After hiring, set their role
      setTimeout(() => {
        const s = gameState.get();
        const newGhost = s.ghosts[s.ghosts.length - 1];
        if (newGhost) {
          dispatch({ type: 'SET_GHOST_ROLE', payload: { ghostInstanceId: newGhost.id, role: sr.role as import('./game/state.js').GhostRole }, timestamp: Date.now() });
        }
      }, 50);
    });
    item.appendChild(btn);
    container.appendChild(item);
  }

  // === Ghost Staff Section ===
  const gstaffTitle = document.createElement('div');
  gstaffTitle.className = 'panel-section-title';
  gstaffTitle.style.marginTop = '16px';
  gstaffTitle.textContent = t('gstaff_title');
  container.appendChild(gstaffTitle);

  const ghostStaffTypes: GhostStaffType[] = ['repairman', 'janitor', 'promoter', 'medic', 'security'];
  for (const staffType of ghostStaffTypes) {
    const def = GHOST_STAFF_DEFS[staffType];

    const item = document.createElement('div');
    item.className = 'shop-item';

    const iconEl = document.createElement('img');
    iconEl.className = 'gstaff-icon';
    iconEl.src = def.image;
    iconEl.alt = t(def.nameKey as Parameters<typeof t>[0]);
    iconEl.draggable = false;
    item.appendChild(iconEl);

    const infoEl = document.createElement('div');
    infoEl.className = 'shop-item-info';

    const nameEl = document.createElement('div');
    nameEl.className = 'shop-item-name';
    nameEl.textContent = t(def.nameKey as Parameters<typeof t>[0]);
    infoEl.appendChild(nameEl);

    const descEl = document.createElement('div');
    descEl.className = 'shop-item-sub';
    descEl.textContent = t(def.descKey as Parameters<typeof t>[0]);
    infoEl.appendChild(descEl);

    const effectEl = document.createElement('div');
    effectEl.className = 'shop-item-sub';
    effectEl.style.color = '#c084fc';
    effectEl.textContent = t(def.effectKey as Parameters<typeof t>[0]);
    infoEl.appendChild(effectEl);

    const costRow = document.createElement('div');
    costRow.className = 'shop-item-cost';
    costRow.textContent = `${t('gstaff_hire_cost', { sp: formatSp(def.hireCost) })} | ${t('gstaff_salary', { sp: formatSp(def.salary) })}`;
    infoEl.appendChild(costRow);

    item.appendChild(infoEl);

    const btn = document.createElement('button');
    btn.className = 'shop-btn';
    btn.disabled = state.sp < def.hireCost;
    btn.textContent = t('hire');
    btn.addEventListener('click', () => {
      dispatch({ type: 'HIRE_GHOST_STAFF', payload: { staffType }, timestamp: Date.now() });
    });
    item.appendChild(btn);

    container.appendChild(item);
  }

  // Show currently hired human staff
  const hiredStaff = state.ghostStaff || [];
  if (hiredStaff.length > 0) {
    const hiredTitle = document.createElement('div');
    hiredTitle.className = 'panel-section-title';
    hiredTitle.style.marginTop = '12px';
    hiredTitle.textContent = `${t('gstaff_title')} (${hiredStaff.length})`;
    container.appendChild(hiredTitle);

    for (const staff of hiredStaff) {
      const def = GHOST_STAFF_DEFS[staff.type];
      const card = document.createElement('div');
      card.className = 'staff-card';
      card.style.cssText = 'display:flex;align-items:center;gap:8px;padding:8px;border-bottom:1px solid #2a2a4a;';

      const iconEl = document.createElement('img');
      iconEl.className = 'gstaff-icon small';
      iconEl.src = def.image;
      iconEl.alt = t(def.nameKey as Parameters<typeof t>[0]);
      iconEl.draggable = false;
      card.appendChild(iconEl);

      const infoEl = document.createElement('div');
      infoEl.style.cssText = 'flex:1;';
      const nameEl = document.createElement('div');
      nameEl.style.cssText = 'font-size:12px;font-weight:600;color:#f0e8ff;';
      nameEl.textContent = `${staff.name} - ${t(def.nameKey as Parameters<typeof t>[0])}`;
      infoEl.appendChild(nameEl);
      const salaryEl = document.createElement('div');
      salaryEl.style.cssText = 'font-size:10px;color:#a08ccc;';
      salaryEl.textContent = t('gstaff_salary', { sp: formatSp(staff.salary) });
      infoEl.appendChild(salaryEl);
      card.appendChild(infoEl);

      const fireBtn = document.createElement('button');
      fireBtn.className = 'popup-btn secondary';
      fireBtn.style.cssText = 'padding:4px 10px;font-size:10px;min-height:28px;';
      fireBtn.textContent = t('gstaff_fire');
      fireBtn.addEventListener('click', () => {
        dispatch({ type: 'FIRE_GHOST_STAFF', payload: { staffId: staff.id }, timestamp: Date.now() });
      });
      card.appendChild(fireBtn);

      container.appendChild(card);
    }
  }
}

// === Staff Panel ===
function renderStaffPanel(container: HTMLElement, state: GameStateData): void {
  container.innerHTML = '';

  if (state.ghosts.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'panel-empty-text';
    empty.textContent = t('staff_empty');
    container.appendChild(empty);
    return;
  }

  for (const ghost of state.ghosts) {
    const def = GHOST_DEFS[ghost.characterId];
    if (!def) continue;

    const card = document.createElement('div');
    card.className = 'staff-card';

    // Avatar
    const avatar = document.createElement('div');
    avatar.className = 'staff-avatar';
    const avatarImg = document.createElement('img');
    avatarImg.className = 'staff-avatar-img';
    avatarImg.src = def.image;
    avatarImg.alt = t(def.nameKey as Parameters<typeof t>[0]);
    avatarImg.draggable = false;
    avatar.appendChild(avatarImg);
    card.appendChild(avatar);

    // Info
    const info = document.createElement('div');
    info.className = 'staff-card-info';

    const nameLine = document.createElement('div');
    nameLine.className = 'staff-card-name';
    nameLine.textContent = t(def.nameKey as Parameters<typeof t>[0]);

    // Grade badge
    const grade = calcStaffGrade(ghost);
    const gradeBadge = document.createElement('span');
    gradeBadge.className = `staff-grade-badge grade-${grade}`;
    gradeBadge.textContent = grade;
    nameLine.appendChild(gradeBadge);

    info.appendChild(nameLine);

    // Role
    const roleText = document.createElement('div');
    roleText.className = 'staff-card-role';
    const roleLabels: Record<string, Parameters<typeof t>[0]> = {
      scare: 'ghost_role_scare',
      repair: 'staff_role_repair',
      janitor: 'staff_role_janitor',
      ticket: 'staff_role_ticket',
      guide: 'staff_role_guide',
      lobby_staff: 'ghost_role_lobby_staff',
      facility_staff: 'ghost_role_facility_staff',
      instructor: 'ghost_role_instructor',
    };
    roleText.textContent = t(roleLabels[ghost.role] ?? 'ghost_role_scare');
    info.appendChild(roleText);

    card.appendChild(info);

    card.addEventListener('click', () => showStaffDetail(ghost));
    container.appendChild(card);
  }
}

function calcStaffGrade(ghost: import('./game/state.js').GhostInstance): import('./game/state.js').StaffGrade {
  const skills = ghost.academySkills;
  const avg = (skills.acting + skills.human_relations + skills.psychology + skills.special_fx + skills.fitness + skills.teamwork + skills.customer_service) / 7;
  const praiseBonus = (ghost.praiseCount ?? 0) * 0.1;
  const total = avg + praiseBonus;
  if (total >= 4.0) return 'S';
  if (total >= 3.0) return 'A';
  if (total >= 2.0) return 'B';
  if (total >= 1.0) return 'C';
  if (total >= 0.5) return 'D';
  return 'F';
}

function showStaffDetail(ghost: import('./game/state.js').GhostInstance): void {
  const def = GHOST_DEFS[ghost.characterId];
  if (!def) return;

  const overlay = document.createElement('div');
  overlay.className = 'popup-overlay';
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });

  const box = document.createElement('div');
  box.className = 'popup-box staff-detail-popup';

  // Big character image
  const imgContainer = document.createElement('div');
  imgContainer.className = 'staff-detail-img-container';
  const img = document.createElement('img');
  img.className = 'staff-detail-img';
  img.src = def.image;
  img.alt = t(def.nameKey as Parameters<typeof t>[0]);
  img.draggable = false;
  imgContainer.appendChild(img);
  box.appendChild(imgContainer);

  // Name
  const nameEl = document.createElement('div');
  nameEl.className = 'staff-detail-name';
  nameEl.textContent = t(def.nameKey as Parameters<typeof t>[0]);
  box.appendChild(nameEl);

  // Type (ghost origin)
  const typeEl = document.createElement('div');
  typeEl.className = 'staff-detail-type';
  typeEl.textContent = `${t('staff_detail_type')}: ${t(def.originKey as Parameters<typeof t>[0])}`;
  box.appendChild(typeEl);

  // Grade
  const grade = calcStaffGrade(ghost);
  const gradeEl = document.createElement('div');
  gradeEl.className = `staff-detail-grade-display grade-color-${grade}`;
  gradeEl.textContent = grade;
  box.appendChild(gradeEl);

  // Skill bars
  const skillsTitle = document.createElement('div');
  skillsTitle.className = 'staff-detail-section-title';
  skillsTitle.textContent = t('staff_detail_skills');
  box.appendChild(skillsTitle);

  const skillDefs: { key: keyof import('./game/state.js').AcademySkills; labelKey: Parameters<typeof t>[0] }[] = [
    { key: 'acting', labelKey: 'skill_acting' },
    { key: 'human_relations', labelKey: 'skill_friendliness' },
    { key: 'psychology', labelKey: 'skill_psychology' },
    { key: 'special_fx', labelKey: 'skill_special_fx' },
    { key: 'fitness', labelKey: 'skill_fitness' },
    { key: 'teamwork', labelKey: 'skill_teamwork' },
    { key: 'customer_service', labelKey: 'skill_service' },
  ];

  for (const sd of skillDefs) {
    const val = ghost.academySkills[sd.key];
    const row = document.createElement('div');
    row.className = 'skill-bar-row';

    const label = document.createElement('span');
    label.className = 'skill-bar-label';
    label.textContent = t(sd.labelKey);
    row.appendChild(label);

    const barOuter = document.createElement('div');
    barOuter.className = 'skill-bar-outer';
    const barFill = document.createElement('div');
    barFill.className = 'skill-bar-fill';
    barFill.style.width = `${(val / 5) * 100}%`;
    barOuter.appendChild(barFill);
    row.appendChild(barOuter);

    const valEl = document.createElement('span');
    valEl.className = 'skill-bar-value';
    valEl.textContent = `${val}/5`;
    row.appendChild(valEl);

    box.appendChild(row);
  }

  // Assigned to
  const assignedEl = document.createElement('div');
  assignedEl.className = 'staff-detail-info-line';
  assignedEl.textContent = `${t('staff_detail_assigned')}: ${ghost.assignedRoomId ? t('ghost_assigned') : t('staff_detail_not_assigned')}`;
  box.appendChild(assignedEl);

  // Hired at
  const hiredEl = document.createElement('div');
  hiredEl.className = 'staff-detail-info-line';
  hiredEl.textContent = t('staff_detail_hired_days', { n: ghost.hiredAt ?? 1 });
  box.appendChild(hiredEl);

  // Praise
  const praiseEl = document.createElement('div');
  praiseEl.className = 'staff-detail-info-line';
  praiseEl.textContent = t('staff_detail_praise', { n: ghost.praiseCount ?? 0 });
  box.appendChild(praiseEl);

  // Close
  const closeBtn = document.createElement('button');
  closeBtn.className = 'popup-btn secondary';
  closeBtn.style.cssText = 'width:100%;min-height:44px;margin-top:12px;';
  closeBtn.textContent = t('close');
  closeBtn.addEventListener('click', () => overlay.remove());
  box.appendChild(closeBtn);

  overlay.appendChild(box);
  popupContainer.appendChild(overlay);
}

// === New Menu Panel ===
function renderNewMenuPanel(container: HTMLElement, _state: GameStateData): void {
  container.innerHTML = '';

  // BGM toggle
  // BGM section
  const bgmSection = document.createElement('div');
  bgmSection.className = 'menu-audio-section';

  const bgmHeader = document.createElement('div');
  bgmHeader.className = 'menu-audio-header';
  const bgmLabel = document.createElement('span');
  bgmLabel.className = 'menu-audio-label';
  bgmLabel.textContent = t('menu_bgm');
  bgmHeader.appendChild(bgmLabel);
  const bgmToggle = document.createElement('button');
  bgmToggle.className = isBgmOn() ? 'menu-audio-toggle on' : 'menu-audio-toggle off';
  bgmToggle.textContent = isBgmOn() ? 'ON' : 'OFF';
  bgmToggle.addEventListener('click', () => {
    const playing = toggleBgm();
    bgmToggle.classList.toggle('on', playing);
    bgmToggle.classList.toggle('off', !playing);
    bgmToggle.textContent = playing ? 'ON' : 'OFF';
    bgmSlider.disabled = !playing;
    bgmSlider.style.opacity = playing ? '1' : '0.4';
  });
  bgmHeader.appendChild(bgmToggle);
  bgmSection.appendChild(bgmHeader);

  const bgmSlider = document.createElement('input');
  bgmSlider.type = 'range';
  bgmSlider.className = 'menu-audio-slider';
  bgmSlider.min = '0';
  bgmSlider.max = '100';
  bgmSlider.value = String(Math.round(getBgmVolume() * 100));
  bgmSlider.disabled = !isBgmOn();
  bgmSlider.style.opacity = isBgmOn() ? '1' : '0.4';
  bgmSlider.addEventListener('input', () => {
    setBgmVolume(Number(bgmSlider.value) / 100);
  });
  bgmSection.appendChild(bgmSlider);
  container.appendChild(bgmSection);

  // SFX section
  const sfxSection = document.createElement('div');
  sfxSection.className = 'menu-audio-section';

  const sfxHeader = document.createElement('div');
  sfxHeader.className = 'menu-audio-header';
  const sfxLabel = document.createElement('span');
  sfxLabel.className = 'menu-audio-label';
  sfxLabel.textContent = t('menu_sfx');
  sfxHeader.appendChild(sfxLabel);
  const sfxToggle = document.createElement('button');
  sfxToggle.className = isSfxOn() ? 'menu-audio-toggle on' : 'menu-audio-toggle off';
  sfxToggle.textContent = isSfxOn() ? 'ON' : 'OFF';
  sfxToggle.addEventListener('click', () => {
    const enabled = toggleSfx();
    sfxToggle.classList.toggle('on', enabled);
    sfxToggle.classList.toggle('off', !enabled);
    sfxToggle.textContent = enabled ? 'ON' : 'OFF';
    sfxSlider.disabled = !enabled;
    sfxSlider.style.opacity = enabled ? '1' : '0.4';
  });
  sfxHeader.appendChild(sfxToggle);
  sfxSection.appendChild(sfxHeader);

  const sfxSlider = document.createElement('input');
  sfxSlider.type = 'range';
  sfxSlider.className = 'menu-audio-slider';
  sfxSlider.min = '0';
  sfxSlider.max = '100';
  sfxSlider.value = '80';
  sfxSlider.disabled = !isSfxOn();
  sfxSlider.style.opacity = isSfxOn() ? '1' : '0.4';
  sfxSlider.addEventListener('input', () => {
    setSfxVolume(Number(sfxSlider.value) / 100);
  });
  sfxSection.appendChild(sfxSlider);
  container.appendChild(sfxSection);

  // Divider
  const divider = document.createElement('div');
  divider.className = 'menu-divider';
  container.appendChild(divider);

  // Language
  const langBtn = document.createElement('button');
  langBtn.className = 'menu-wide-btn';
  langBtn.textContent = t('language');
  langBtn.addEventListener('click', () => {
    closeTabPopup();
    showLanguagePicker(popupContainer);
  });
  container.appendChild(langBtn);

  // Game Reset
  const resetBtn = document.createElement('button');
  resetBtn.className = 'menu-wide-btn danger-outline';
  resetBtn.textContent = t('settings_reset');
  resetBtn.addEventListener('click', () => {
    closeTabPopup();
    const overlay = document.createElement('div');
    overlay.className = 'popup-overlay';
    overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
    const box = document.createElement('div');
    box.className = 'popup-box';
    const msg = document.createElement('div');
    msg.className = 'popup-message';
    msg.style.cssText = 'text-align:center;margin-bottom:16px;';
    msg.textContent = t('settings_reset_confirm');
    box.appendChild(msg);
    const btns = document.createElement('div');
    btns.className = 'popup-buttons';
    const cancelBtn = document.createElement('button');
    cancelBtn.className = 'popup-btn secondary';
    cancelBtn.textContent = t('cancel');
    cancelBtn.addEventListener('click', () => overlay.remove());
    btns.appendChild(cancelBtn);
    const confirmBtn = document.createElement('button');
    confirmBtn.className = 'popup-btn primary';
    confirmBtn.style.background = 'linear-gradient(135deg,#ef4444,#dc2626)';
    confirmBtn.textContent = t('confirm');
    confirmBtn.addEventListener('click', () => {
      gameState.reset();
      window.location.reload();
    });
    btns.appendChild(confirmBtn);
    box.appendChild(btns);
    overlay.appendChild(box);
    popupContainer.appendChild(overlay);
  });
  container.appendChild(resetBtn);

  // Game info
  const infoEl = document.createElement('div');
  infoEl.className = 'menu-info-text';
  infoEl.textContent = t('menu_version', { v: '2.0.0' });
  container.appendChild(infoEl);
}

// === Render Panel ===
function renderCurrentPanel(state: GameStateData): void {
  if (!tabPopupContentEl || !currentTab) return;

  switch (currentTab) {
    case 'facilities_mgmt':
      renderFacilitiesMgmtPanel(tabPopupContentEl, state);
      break;
    case 'hire':
      renderHirePanel(tabPopupContentEl, state);
      break;
    case 'staff':
      renderStaffPanel(tabPopupContentEl, state);
      break;
    case 'menu':
      renderNewMenuPanel(tabPopupContentEl, state);
      break;
    case 'support_facilities':
      renderFacilitiesView(tabPopupContentEl, state, (ev) => {
        facilitiesActionHandler(ev);
        renderCurrentPanel(gameState.get());
      });
      break;
    case 'achievements':
      renderAchievementsPanel(tabPopupContentEl, state);
      break;
  }
}

function renderAchievementsPanel(container: HTMLElement, _state: GameStateData): void {
  container.innerHTML = '';
  const placeholder = document.createElement('div');
  placeholder.className = 'achievements-placeholder';
  placeholder.innerHTML = `<div class="achievements-placeholder-icon"><img src="/images/ui/ui-star-filled.webp" alt="" draggable="false" style="width:48px;height:48px;opacity:0.5" /></div><div class="achievements-placeholder-text">${t('achievements_coming_soon')}</div>`;
  container.appendChild(placeholder);
}

// === World View Tab Bar ===
let _isWorldTabBar = false;
const WORLD_TAB_CONFIG = [
  { tab: 'support_facilities', icon: '/images/ui/tab-build.webp', labelKey: 'tab_support_facilities' as const },
  { tab: 'achievements', icon: '/images/ui/ui-star-filled.webp', labelKey: 'tab_achievements' as const },
  { tab: 'menu', icon: '/images/ui/ui-icon-menu.webp', labelKey: 'menu_tab' as const },
];
const BUILDING_TAB_CONFIG = [
  { tab: 'facilities_mgmt', icon: '/images/ui/tab-build.webp', labelKey: 'tab_facilities' as const },
  { tab: 'hire', icon: '/images/ui/tab-shop.webp', labelKey: 'tab_hire' as const },
  { tab: 'staff', icon: '/images/ui/tab-ghost.webp', labelKey: 'tab_staff' as const },
  { tab: 'menu', icon: '/images/ui/ui-icon-menu.webp', labelKey: 'menu_tab' as const },
];

function updateTabBar(isWorld: boolean): void {
  if (_isWorldTabBar === isWorld) return;
  _isWorldTabBar = isWorld;
  const tabBar = document.getElementById('tab-bar')!;
  const config = isWorld ? WORLD_TAB_CONFIG : BUILDING_TAB_CONFIG;
  tabBar.innerHTML = '';
  config.forEach((item, i) => {
    const btn = document.createElement('button');
    btn.className = `tab-btn${i === 0 ? ' active' : ''}`;
    btn.dataset.tab = item.tab;
    btn.id = `tab-${item.tab}`;
    btn.innerHTML = `<span class="tab-icon"><img class="tab-icon-img" src="${item.icon}" alt="" draggable="false" /></span><span class="tab-label">${t(item.labelKey)}</span>`;
    btn.addEventListener('click', () => {
      openTabPopup(item.tab);
    });
    tabBar.appendChild(btn);
  });
}

function showPrestigeConfirm(state: GameStateData): void {
  const req = CONFIG.prestigeMinFloors;
  const reqFame = CONFIG.prestigeMinFame;
  const reqSp = CONFIG.prestigeMinTotalSp;
  const canPrestige = state.building.length >= req && state.fame >= reqFame && state.totalSpEarned >= reqSp;
  if (!canPrestige) return;

  const overlay = document.createElement('div');
  overlay.className = 'popup-overlay';

  const box = document.createElement('div');
  box.className = 'popup-box';

  const title = document.createElement('div');
  title.className = 'popup-title';
  title.textContent = t('prestige_title');
  box.appendChild(title);

  const msg = document.createElement('div');
  msg.className = 'popup-message';
  msg.textContent = t('prestige_desc');
  box.appendChild(msg);

  const btnRow = document.createElement('div');
  btnRow.className = 'popup-buttons';

  const cancelBtn = document.createElement('button');
  cancelBtn.className = 'popup-btn secondary';
  cancelBtn.textContent = t('cancel');
  cancelBtn.addEventListener('click', () => overlay.remove());

  const confirmBtn = document.createElement('button');
  confirmBtn.className = 'popup-btn primary';
  confirmBtn.textContent = t('prestige_confirm');
  confirmBtn.addEventListener('click', () => {
    const newPrestigeLevel = state.prestigeLevel + 1;
    const startSp = newPrestigeLevel * CONFIG.prestigeStartSpPerLevel;

    gameState.reset({
      prestigeLevel: newPrestigeLevel,
      prestigeCoins: state.prestigeCoins + newPrestigeLevel,
      sp: startSp,
      building: [{
        level: 1,
        room: null,
      }],
    });
    overlay.remove();
  });

  btnRow.appendChild(cancelBtn);
  btnRow.appendChild(confirmBtn);
  box.appendChild(btnRow);
  overlay.appendChild(box);
  popupContainer.appendChild(overlay);
}

// === Ghost Speech Bubble System ===
let _lastSpeechAt = 0;
const SPEECH_INTERVAL_MS = 8000; // show a speech bubble every 8 seconds
const SPEECH_DURATION_MS = 3500;
const _activeSpeechFloors = new Set<number>();

function renderGhostSpeechBubbles(state: GameStateData): void {
  if (state.dayPhase !== 'day') return; // only during idle phase
  if (state.isOpen) return; // not during evening operations

  const now = Date.now();
  if (now - _lastSpeechAt < SPEECH_INTERVAL_MS) return;

  // Find ghosts in rooms that don't have active speech
  const candidates: { floorLevel: number; ghostDef: any; ghost: any }[] = [];
  for (const floor of state.building) {
    if (!floor.room || !floor.room.assignedGhostId) continue;
    if (_activeSpeechFloors.has(floor.level)) continue;
    const ghost = state.ghosts.find(g => g.id === floor.room!.assignedGhostId);
    if (!ghost) continue;
    const ghostDef = GHOST_DEFS[ghost.characterId];
    if (!ghostDef) continue;
    candidates.push({ floorLevel: floor.level, ghostDef, ghost });
  }

  if (candidates.length === 0) return;

  // Pick a random ghost
  const pick = candidates[Math.floor(Math.random() * candidates.length)];
  _lastSpeechAt = now;
  _activeSpeechFloors.add(pick.floorLevel);

  const quoteKey = pick.ghost.fatigue >= 80 ? pick.ghostDef.fatiguedQuoteKey : pick.ghostDef.quoteKey;
  const text = t(quoteKey as any);

  // Find the floor DOM element
  const floorEl = document.querySelector(`#floor-${pick.floorLevel}`);
  if (!floorEl) {
    _activeSpeechFloors.delete(pick.floorLevel);
    return;
  }

  const bubble = document.createElement('div');
  bubble.className = 'ghost-speech-bubble';
  bubble.textContent = text;
  (floorEl as HTMLElement).style.position = 'relative';
  floorEl.appendChild(bubble);

  setTimeout(() => {
    bubble.classList.add('fading');
    setTimeout(() => {
      bubble.remove();
      _activeSpeechFloors.delete(pick.floorLevel);
    }, 500);
  }, SPEECH_DURATION_MS);
}

// === Scare Popup Rendering ===
const _renderedPopupIds = new Set<string>();

function renderScarePopups(state: GameStateData): void {
  let layer = document.getElementById('scare-layer');
  if (!layer) {
    layer = document.createElement('div');
    layer.id = 'scare-layer';
    document.getElementById('app')!.appendChild(layer);
  }

  // Chain combo indicator
  const chainCount = state.visitorGroup?.scareChainCount ?? 0;
  let comboEl = layer.querySelector<HTMLElement>('.chain-combo');
  if (chainCount >= 2) {
    if (!comboEl) {
      comboEl = document.createElement('div');
      comboEl.className = 'chain-combo';
      layer.appendChild(comboEl);
    }
    comboEl.textContent = `COMBO x${chainCount}`;
    comboEl.className = `chain-combo chain-level-${Math.min(chainCount, 5)}`;
  } else if (comboEl) {
    comboEl.remove();
  }

  // Screen flash on high chain
  if (chainCount >= 3 && state.scarePopups.some(p => !_renderedPopupIds.has(p.id))) {
    let flash = document.getElementById('chain-flash');
    if (!flash) {
      flash = document.createElement('div');
      flash.id = 'chain-flash';
      flash.className = 'chain-flash';
      document.getElementById('app')!.appendChild(flash);
    }
    flash.classList.remove('active');
    void flash.offsetWidth; // force reflow
    flash.classList.add('active');
  }

  for (const popup of state.scarePopups) {
    if (_renderedPopupIds.has(popup.id)) continue;
    _renderedPopupIds.add(popup.id);

    const el = document.createElement('div');
    el.className = popup.skillActivated ? 'scare-popup skill-active' : 'scare-popup';

    const spText = `+${popup.spEarned}`;
    if (popup.skillActivated) {
      el.innerHTML = `${spText} <span class="skill-badge">SKILL!</span>`;
      playSkillActivate();
    } else if (popup.spEarned > 50) {
      playChainBonus(Math.floor(popup.spEarned / 50));
    } else {
      playScare();
    }

    // Position above the floor
    const buildingRect = document.getElementById('building')?.getBoundingClientRect();
    if (buildingRect) {
      const floorCount = state.building.length;
      const floorHeight = buildingRect.height / Math.max(1, floorCount);
      const floorIndex = state.building.findIndex(f => f.level === popup.floorLevel);
      const yOffset = buildingRect.top + (floorCount - 1 - floorIndex) * floorHeight - 10;
      const xOffset = buildingRect.left + buildingRect.width * (0.3 + Math.random() * 0.4);
      el.style.left = `${xOffset}px`;
      el.style.top = `${yOffset}px`;
    }

    layer.appendChild(el);

    // Remove after animation
    setTimeout(() => {
      el.remove();
      _renderedPopupIds.delete(popup.id);
    }, 2200);
  }
}

// === Review Processing ===
function processReviews(): void {
  const review = consumePendingReview();
  if (review) {
    dispatch({
      type: 'ADD_REVIEW',
      payload: {
        stars: review.stars,
        textKey: review.textKey,
        ghostNameKey: review.ghostNameKey,
        visitorType: review.visitorType,
        floorLevel: review.floorLevel,
        satisfaction: review.satisfaction,
      },
      timestamp: Date.now(),
    });
  }
}

// === Academy Auto-Complete Check ===
function checkAcademyCompletions(): void {
  const state = gameState.get();
  const now = Date.now();
  for (const ghost of state.ghosts) {
    if (ghost.academySession && now >= ghost.academySession.completesAt) {
      dispatch({ type: 'COMPLETE_ACADEMY_TRAINING', payload: { ghostInstanceId: ghost.id }, timestamp: Date.now() });
    }
  }
}

// === Training Auto-Complete Check ===
function checkTrainingCompletions(): void {
  const state = gameState.get();
  const now = Date.now();
  for (const ghost of state.ghosts) {
    if (ghost.training && now >= ghost.training.completesAt) {
      dispatch({ type: 'COMPLETE_TRAINING', payload: { ghostInstanceId: ghost.id }, timestamp: Date.now() });
    }
  }
}

// === Random Event Popup ===
let _activeEventOverlay: HTMLElement | null = null;

function processRandomEvents(): void {
  const pending = consumePendingEvent();
  if (!pending) return;

  const eventDef = getEventById(pending.eventId);
  if (!eventDef) return;

  if (pending.defended) {
    showDefendedEventPopup(pending, eventDef);
  } else {
    showEventPopup(pending, eventDef);
  }
}

function showEventPopup(
  active: ActiveEvent,
  eventDef: ReturnType<typeof getEventById>,
): void {
  if (!eventDef) return;
  // Remove old overlay if exists
  if (_activeEventOverlay) {
    _activeEventOverlay.remove();
    _activeEventOverlay = null;
  }

  const overlay = document.createElement('div');
  overlay.className = 'popup-overlay';
  _activeEventOverlay = overlay;

  const box = document.createElement('div');
  box.className = 'popup-box';
  box.style.cssText = 'max-width:360px;';

  // Title
  const title = document.createElement('div');
  title.className = 'popup-title';
  title.textContent = t(
    eventDef.nameKey as Parameters<typeof t>[0],
  );
  box.appendChild(title);

  // Description
  const desc = document.createElement('div');
  desc.className = 'popup-message';
  desc.style.cssText = 'margin-bottom:12px;line-height:1.5;';
  desc.textContent = t(
    eventDef.descKey as Parameters<typeof t>[0],
  );
  box.appendChild(desc);

  // Countdown timer (if timed)
  let timerEl: HTMLElement | null = null;
  let timerInterval = 0;
  if (active.expiresAt > 0) {
    timerEl = document.createElement('div');
    timerEl.style.cssText =
      'text-align:center;font-size:14px;color:#f59e0b;margin-bottom:8px;';
    box.appendChild(timerEl);

    const updateTimer = () => {
      const remaining = Math.max(
        0,
        Math.ceil((active.expiresAt - Date.now()) / 1000),
      );
      if (timerEl) {
        timerEl.textContent = t('evt_time_remaining', {
          sec: remaining,
        });
      }
      if (remaining <= 0) {
        clearInterval(timerInterval);
        // Auto-resolve
        if (
          eventDef.autoResolve !== undefined &&
          _activeEventOverlay === overlay
        ) {
          resolveEvent(
            active.eventId,
            eventDef.autoResolve,
            overlay,
          );
        }
      }
    };
    updateTimer();
    timerInterval = window.setInterval(updateTimer, 500);
  }

  // Choice buttons
  const btnRow = document.createElement('div');
  btnRow.style.cssText =
    'display:flex;flex-direction:column;gap:8px;';

  eventDef.choices.forEach((choice, idx) => {
    const btn = document.createElement('button');
    btn.className = idx === 0 ? 'popup-btn primary' : 'popup-btn secondary';
    btn.style.cssText = 'width:100%;min-height:44px;';
    btn.textContent = t(
      choice.labelKey as Parameters<typeof t>[0],
    );
    btn.addEventListener('click', () => {
      if (timerInterval) clearInterval(timerInterval);
      resolveEvent(active.eventId, idx, overlay);
    });
    btnRow.appendChild(btn);
  });

  box.appendChild(btnRow);

  // Defense hint: show what the player needs to auto-defend next time
  if (eventDef.defense && !active.defended) {
    const hintEl = document.createElement('div');
    hintEl.style.cssText =
      'margin-top:10px;padding:8px 12px;border-radius:6px;' +
      'background:rgba(59,130,246,0.15);border:1px solid rgba(59,130,246,0.3);' +
      'font-size:12px;color:#93c5fd;line-height:1.4;';
    hintEl.textContent = t(
      eventDef.defense.hintKey as Parameters<typeof t>[0],
    );
    box.appendChild(hintEl);
  }

  overlay.appendChild(box);
  popupContainer.appendChild(overlay);
}

function showDefendedEventPopup(
  active: ActiveEvent,
  eventDef: ReturnType<typeof getEventById>,
): void {
  if (!eventDef) return;
  if (_activeEventOverlay) {
    _activeEventOverlay.remove();
    _activeEventOverlay = null;
  }

  const overlay = document.createElement('div');
  overlay.className = 'popup-overlay';
  _activeEventOverlay = overlay;

  const box = document.createElement('div');
  box.className = 'popup-box';
  box.style.cssText = 'max-width:360px;border:2px solid #22c55e;';

  // Auto-defense badge
  const badge = document.createElement('div');
  badge.style.cssText =
    'text-align:center;padding:4px 12px;margin-bottom:8px;' +
    'background:#166534;border-radius:6px;font-size:13px;' +
    'color:#bbf7d0;font-weight:bold;';
  badge.textContent = t('evt_defense_badge');
  box.appendChild(badge);

  // Title
  const title = document.createElement('div');
  title.className = 'popup-title';
  title.textContent = t(
    eventDef.nameKey as Parameters<typeof t>[0],
  );
  box.appendChild(title);

  // Defense message
  const defMsg = document.createElement('div');
  defMsg.className = 'popup-message';
  defMsg.style.cssText = 'margin-bottom:12px;line-height:1.5;color:#86efac;';
  defMsg.textContent = eventDef.defense
    ? t(eventDef.defense.defenseKey as Parameters<typeof t>[0])
    : '';
  box.appendChild(defMsg);

  // Bonus SP display
  const bonus = eventDef.defense?.bonusReward || 0;
  if (bonus > 0) {
    const bonusEl = document.createElement('div');
    bonusEl.style.cssText =
      'text-align:center;font-size:16px;font-weight:bold;' +
      'color:#fbbf24;margin-bottom:8px;';
    bonusEl.textContent = t('evt_defense_bonus', { sp: bonus });
    box.appendChild(bonusEl);
  }

  // OK button
  const okBtn = document.createElement('button');
  okBtn.className = 'popup-btn primary';
  okBtn.style.cssText = 'width:100%;min-height:44px;background:#166534;border-color:#22c55e;';
  okBtn.textContent = t('ok');
  okBtn.addEventListener('click', () => {
    dispatch({
      type: 'DISMISS_DEFENDED_EVENT',
      payload: { eventId: active.eventId },
      timestamp: Date.now(),
    });
    overlay.remove();
    if (_activeEventOverlay === overlay) {
      _activeEventOverlay = null;
    }
  });
  box.appendChild(okBtn);

  overlay.appendChild(box);
  popupContainer.appendChild(overlay);
}

function resolveEvent(
  eventId: string,
  choiceIndex: number,
  overlay: HTMLElement,
): void {
  dispatch({
    type: 'RESOLVE_EVENT',
    payload: { eventId, choiceIndex },
    timestamp: Date.now(),
  });

  const eventDef = getEventById(eventId);
  const choice = eventDef?.choices[choiceIndex];

  if (choice) {
    // Replace overlay content with result message
    const box = overlay.querySelector('.popup-box');
    if (box) {
      box.innerHTML = '';

      const resultTitle = document.createElement('div');
      resultTitle.className = 'popup-title';
      resultTitle.textContent = t(
        eventDef!.nameKey as Parameters<typeof t>[0],
      );
      box.appendChild(resultTitle);

      const resultMsg = document.createElement('div');
      resultMsg.className = 'popup-message';
      resultMsg.style.cssText = 'margin-bottom:12px;line-height:1.5;';
      resultMsg.textContent = t(
        choice.resultKey as Parameters<typeof t>[0],
      );
      box.appendChild(resultMsg);

      const okBtn = document.createElement('button');
      okBtn.className = 'popup-btn primary';
      okBtn.style.cssText = 'width:100%;min-height:44px;';
      okBtn.textContent = t('ok');
      okBtn.addEventListener('click', () => {
        overlay.remove();
        if (_activeEventOverlay === overlay) {
          _activeEventOverlay = null;
        }
      });
      box.appendChild(okBtn);
    }
  } else {
    overlay.remove();
    if (_activeEventOverlay === overlay) {
      _activeEventOverlay = null;
    }
  }
}

// === Story Arc Guide ===
const STORY_GUIDE_SHOWN_KEY = 'haunted-house-story-guide-shown';
let storyGuideShown = !!localStorage.getItem(STORY_GUIDE_SHOWN_KEY);

function checkStoryArcGuide(state: GameStateData): void {
  if (storyGuideShown) return;
  const builtRooms = state.building.filter((f) => f.room !== null).length;
  if (builtRooms < 4) return;

  storyGuideShown = true;
  localStorage.setItem(STORY_GUIDE_SHOWN_KEY, '1');

  // Show guide popup
  const overlay = document.createElement('div');
  overlay.className = 'popup-overlay';

  const box = document.createElement('div');
  box.className = 'popup-box';

  const title = document.createElement('div');
  title.className = 'popup-title';
  title.textContent = t('story_guide_title');
  box.appendChild(title);

  const desc = document.createElement('div');
  desc.className = 'popup-message';
  desc.textContent = t('story_guide_desc');
  box.appendChild(desc);

  // Role descriptions
  const roles = [
    { key: 'story_intro', descKey: 'story_intro_desc', cls: 'role-intro' },
    { key: 'story_rising', descKey: 'story_rising_desc', cls: 'role-rising' },
    { key: 'story_climax', descKey: 'story_climax_desc', cls: 'role-climax' },
    { key: 'story_resolution', descKey: 'story_resolution_desc', cls: 'role-resolution' },
  ] as const;

  const roleList = document.createElement('div');
  roleList.style.cssText = 'margin: 12px 0; text-align: left;';
  for (const role of roles) {
    const row = document.createElement('div');
    row.style.cssText = 'display: flex; align-items: center; gap: 8px; margin: 6px 0;';

    const badge = document.createElement('span');
    badge.className = `story-role-label ${role.cls}`;
    badge.style.cssText = 'position: static; transform: none;';
    badge.textContent = t(role.key);
    row.appendChild(badge);

    const text = document.createElement('span');
    text.style.cssText = 'font-size: 12px; color: var(--text-secondary);';
    text.textContent = t(role.descKey);
    row.appendChild(text);

    roleList.appendChild(row);
  }
  box.appendChild(roleList);

  const okBtn = document.createElement('button');
  okBtn.className = 'popup-btn primary';
  okBtn.style.cssText = 'width:100%;min-height:44px;';
  okBtn.textContent = t('ok');
  okBtn.addEventListener('click', () => overlay.remove());
  box.appendChild(okBtn);

  overlay.appendChild(box);
  popupContainer.appendChild(overlay);
}

// === UI Update ===
let renderScheduled = false;
let lastRenderedState: GameStateData | null = null;

// === Day Phase Actions ===
let _dayPhaseActionsEl: HTMLElement | null = null;
let _nightSummaryShown = false;

function showToast(message: string): void {
  const el = document.createElement('div');
  el.className = 'game-toast';
  el.textContent = message;
  document.body.appendChild(el);
  setTimeout(() => el.classList.add('show'), 10);
  setTimeout(() => { el.classList.remove('show'); setTimeout(() => el.remove(), 300); }, 2500);
}

// === Facilities Fullscreen View ===
let _facilitiesFullscreen = false;
let _facilitiesRenderInterval: ReturnType<typeof setInterval> | null = null;

function facilitiesActionHandler(ev: import('./ui/facilitiesView.js').FacilitiesViewAction): void {
  if (ev.action === 'build_on_plot') {
    dispatch({ type: 'BUILD_FACILITY_ON_PLOT', payload: { plotId: ev.plotId, facilityType: ev.facilityType }, timestamp: Date.now() });
  } else if (ev.action === 'build_haunted_house_on_plot') {
    // Build haunted house then set its theme
    dispatch({ type: 'BUILD_FACILITY_ON_PLOT', payload: { plotId: ev.plotId, facilityType: 'haunted_house' as any }, timestamp: Date.now() });
    // Find the buildingId that was just assigned to this plot
    const updatedState = gameState.get();
    const builtPlot = updatedState.facilityMap[ev.plotId];
    if (builtPlot?.facility?.buildingId) {
      dispatch({ type: 'SET_HAUNTED_BUILDING_THEME', payload: { buildingId: builtPlot.facility.buildingId, themeId: ev.themeId as any }, timestamp: Date.now() });
    }
  } else if (ev.action === 'upgrade_plot') {
    dispatch({ type: 'UPGRADE_FACILITY_PLOT', payload: { plotId: ev.plotId }, timestamp: Date.now() });
  } else if (ev.action === 'unlock_plot') {
    dispatch({ type: 'UNLOCK_FACILITY_PLOT', payload: { plotId: ev.plotId }, timestamp: Date.now() });
  } else if (ev.action === 'assign_plot_staff') {
    dispatch({ type: 'ASSIGN_FACILITY_PLOT_STAFF', payload: { plotId: ev.plotId, ghostInstanceId: ev.ghostInstanceId }, timestamp: Date.now() });
  } else if (ev.action === 'unassign_plot_staff') {
    dispatch({ type: 'UNASSIGN_FACILITY_PLOT_STAFF', payload: { plotId: ev.plotId }, timestamp: Date.now() });
  } else if (ev.action === 'set_price') {
    dispatch({ type: 'SET_FACILITY_PRICE', payload: { plotId: ev.plotId, price: ev.price }, timestamp: Date.now() });
  } else if (ev.action === 'demolish') {
    dispatch({ type: 'DEMOLISH_FACILITY', payload: { plotId: ev.plotId }, timestamp: Date.now() });
  } else if (ev.action === 'toggle_facility_open') {
    dispatch({ type: 'TOGGLE_FACILITY_OPEN', payload: { plotId: ev.plotId }, timestamp: Date.now() });
  } else if (ev.action === 'move_facility') {
    dispatch({ type: 'MOVE_FACILITY', payload: { fromPlotId: ev.fromPlotId, toPlotId: ev.toPlotId }, timestamp: Date.now() });
  } else if (ev.action === 'enter_haunted_house') {
    // Navigate to world screen (building selection list)
    dispatch({ type: 'SET_WORLD_SCREEN', payload: { screen: 'world' }, timestamp: Date.now() });
    closeFacilitiesFullscreen();
    return;
  } else if (ev.action === 'open_park') {
    // Ticket booth: start evening — requires at least 1 ghost assigned to a room
    const state = gameState.get();
    if (state.dayPhase === 'day') {
      const selectedBuilding = state.hauntedBuildings?.find(b => b.id === state.selectedHauntedBuildingId) ?? state.hauntedBuildings?.[0];
      const hasGhostInRoom = selectedBuilding?.building?.some(floor => floor.room?.assignedGhostId) ?? false;
      if (!hasGhostInRoom) {
        showToast(t('need_ghost_to_open'));
        return;
      }
      dispatch({ type: 'START_EVENING', payload: {}, timestamp: Date.now() });
    }
  } else if (ev.action === 'ticket_booth_info') {
    // Show ticket booth info popup (during business hours)
    showFacilityInfoPopup(gameState.get().facilityMap[ev.plotId], gameState.get(), facilitiesActionHandler);
    return;
  }
  // Re-render after action
  renderFacilitiesContent();
}

function renderFacilitiesContent(): void {
  const state = gameState.get();
  renderFacilitiesView(facilitiesContent, state, facilitiesActionHandler);
}

function openFacilitiesFullscreen(): void {
  if (_facilitiesFullscreen) return;
  _facilitiesFullscreen = true;

  // Hide world screen, show facilities container fullscreen
  worldScreen.style.display = 'none';
  facilitiesContainer.style.display = '';
  document.getElementById('view-switcher')!.style.display = 'none';

  // Add back button
  let backBtn = document.getElementById('facilities-back-btn');
  if (!backBtn) {
    backBtn = document.createElement('button');
    backBtn.id = 'facilities-back-btn';
    backBtn.className = 'facilities-back-btn';
    backBtn.innerHTML = `<span style="margin-right:4px">&larr;</span> ${t('back')}`;
    backBtn.addEventListener('click', closeFacilitiesFullscreen);
    facilitiesContainer.prepend(backBtn);
  }
  backBtn.style.display = '';

  // Initial render
  renderFacilitiesContent();

  // Periodic re-render for visitor token animation (every 500ms)
  if (_facilitiesRenderInterval) clearInterval(_facilitiesRenderInterval);
  _facilitiesRenderInterval = setInterval(renderFacilitiesContent, 500);
}

function closeFacilitiesFullscreen(): void {
  if (!_facilitiesFullscreen) return;
  _facilitiesFullscreen = false;

  if (_facilitiesRenderInterval) {
    clearInterval(_facilitiesRenderInterval);
    _facilitiesRenderInterval = null;
  }

  facilitiesContainer.style.display = 'none';
  worldScreen.style.display = '';
  updateTabBar(true);

  const backBtn = document.getElementById('facilities-back-btn');
  if (backBtn) backBtn.style.display = 'none';
}

function openTabPopup(tab: string): void {
  if (tabPopupClosing) return;

  // Support facilities: fullscreen view instead of popup
  if (tab === 'support_facilities') {
    closeTabPopup();
    openFacilitiesFullscreen();
    return;
  }

  // Already open with same tab — close it (toggle)
  if (currentTab === tab && tabPopupOverlay) {
    closeTabPopup();
    return;
  }

  currentTab = tab;

  // Update tab bar active state
  document.querySelectorAll('.tab-btn').forEach((b) => b.classList.remove('active'));
  document.querySelector(`.tab-btn[data-tab="${tab}"]`)?.classList.add('active');

  // If popup already open, just swap content
  if (tabPopupOverlay && tabPopupSheet && tabPopupContentEl) {
    const titleEl = tabPopupSheet.querySelector('.tab-popup-title');
    if (titleEl) {
      const titleKey = TAB_TITLES[tab];
      titleEl.textContent = titleKey ? t(titleKey) : tab;
    }
    tabPopupContentEl.innerHTML = '';
    renderCurrentPanel(gameState.get());
    return;
  }

  // Create overlay
  tabPopupOverlay = document.createElement('div');
  tabPopupOverlay.className = 'tab-popup-overlay';
  tabPopupOverlay.addEventListener('click', (e) => {
    if (e.target === tabPopupOverlay) closeTabPopup();
  });

  // Create center popup (not bottom sheet)
  tabPopupSheet = document.createElement('div');
  tabPopupSheet.className = 'tab-popup-center';

  // Header
  const header = document.createElement('div');
  header.className = 'tab-popup-header';

  const titleEl = document.createElement('div');
  titleEl.className = 'tab-popup-title';
  const titleKey = TAB_TITLES[tab];
  titleEl.textContent = titleKey ? t(titleKey) : tab;
  header.appendChild(titleEl);

  const closeBtn = document.createElement('button');
  closeBtn.className = 'tab-popup-close-btn';
  closeBtn.textContent = 'X';
  closeBtn.setAttribute('aria-label', t('close'));
  closeBtn.addEventListener('click', () => closeTabPopup());
  header.appendChild(closeBtn);

  tabPopupSheet.appendChild(header);

  // Content area
  tabPopupContentEl = document.createElement('div');
  tabPopupContentEl.className = 'tab-popup-content';
  tabPopupSheet.appendChild(tabPopupContentEl);

  // No swipe handling needed for center popup
  // Mount to body (not game-ui) so position:fixed works correctly
  document.body.appendChild(tabPopupOverlay);
  document.body.appendChild(tabPopupSheet);

  // Trigger slide-up animation on next frame
  requestAnimationFrame(() => {
    tabPopupOverlay?.classList.add('visible');
    tabPopupSheet?.classList.add('visible');
  });

  // Render content
  renderCurrentPanel(gameState.get());
}

// === Story Arc: recalculate and patch state ===
{
  const _noop_story_arc_anchor = 0;
  void _noop_story_arc_anchor;
}

/* removed duplicate old render block */

gameState.subscribe((state) => {
  scheduleRender(state);
});

// Initialize dayPhaseStartedAt if not set
{
  const s = gameState.get();
  if (!s.dayPhaseStartedAt) {
    gameState.patch({ dayPhaseStartedAt: Date.now() } as any);
  }
}

// Initial render
doRender(gameState.get());

// Auto-open facilities fullscreen as main hub when on world screen
{
  const s = gameState.get();
  if (s.worldScreen === 'world') {
    openFacilitiesFullscreen();
  }
}

// Tutorial: check if we need to start/resume tutorial
checkTutorialProgress();

// === Buy Floor from building view ===
window.addEventListener('buy-floor', () => {
  dispatch({ type: 'BUY_FLOOR', payload: {}, timestamp: Date.now() });
});

// === HUD Day Display Click ===

// === Settings (moved to menu) ===

// === Offline Earnings (removed) ===

// === Background Audio Handling ===
document.addEventListener('visibilitychange', () => {
  // Placeholder for future BGM support
});

// === Game Loop ===
startGameLoop(() => gameState.get(), (s) => gameState.set(s));

// === Demo API ===
fetch('/demo-data/demo-01.json')
  .then((r) => {
    if (!r.ok) throw new Error('no demo data');
    return r.json();
  })
  .then((data) => {
    demoPlayer.load(data);

    (window as any).__demo = {
      start: () => { gameState.reset(); demoPlayer.play(); },
      stop: () => demoPlayer.stop(),
      isPlaying: () => demoPlayer.isPlaying(),
    };

    if (new URLSearchParams(window.location.search).get('demo') === '1') {
      gameState.reset();
      demoPlayer.play();
    }
  })
  .catch(() => { /* No demo data */ });

// === Dev Mode ===
if (import.meta.env.DEV) {
  (window as any).__gameState = gameState;
  (window as any).__executor = executor;
  (window as any).__recorder = recorder;
  (window as any).__demoPlayer = demoPlayer;
}

// Keep legacy functions accessible for potential future use
void handleGhostClick;
void showDaySummary;
void renderMenuPanel;
